// Coplanar Hohmann transfer solver: what a trip to another body costs, when it
// leaves, and how long it takes.
//
// Reads the world registry (src/world/bodies.ts) and its propagator
// (src/world/ephemeris.ts) read-only and returns one plan per destination: the
// burns, the total, the time of flight and the next departure window. Pure by
// construction - no three.js, no DOM, no module state - so tools/preview runs
// it under Node with types stripped, the way ephem-check.mjs runs the
// propagator.
//
// The model, stated here because the map has to state it on its own face:
//
//   - Two impulses in the heliocentric frame on a coplanar Hohmann ellipse
//     between circular orbits at the origin's and the destination's own
//     semi-major axes. That layer is `heliocentricDvKmS`, and it is the layer
//     an autopilot flies: each burn carries a unit `direction` in ecliptic
//     J2000 at its own epoch.
//   - What an impulse COSTS depends on where the ship starts and ends. Free in
//     space it costs the impulse itself. Leaving a body it costs the VECTOR
//     difference between the velocity the escape hyperbola needs at the ship's
//     own point and the velocity it has there. Energy fixes that velocity's
//     magnitude at sqrt(vInf^2 + 2 GM/r); the transfer fixes its direction,
//     since the conic has to come out along the transfer's own asymptote, and
//     from one point two conics do that, one each way round the body. WHICH
//     point is the rule below: a window is days to hundreds of days out and a
//     low orbit's period is hours, so a ship on a closed orbit that clears the
//     surface is priced at the cheapest point of one revolution rather than
//     wherever it happens to be when the plan is asked for, which swings the
//     Earth-Mars departure from 1.2 to 20.0 km/s round one lap. Arriving
//     it costs the insertion burn at periapsis of the capture orbit. That is
//     `dvKmS`, and it is the number a destination's own mass moves: Mars
//     capture from a 400 km parking orbit is 2.080 km/s against 2.649 km/s for
//     the same arrival at a massless Mars, which is what makes the mass a
//     readable term rather than decoration.
//   - A ship inside a body's sphere of influence is on that body's heliocentric
//     orbit, so the whole heliocentric side of the plan comes from the body's
//     own row and only the burn costs come from the ship's live state. A moon's
//     own row carries a PARENT-RELATIVE orbit and no heliocentric radius at
//     all, so a ship inside a moon's sphere of influence plans from the moon's
//     parent instead: the planet's heliocentric orbit, the ship's state taken
//     against the planet. A ship outside every sphere of influence is planned
//     from its own position and velocity, and both burns are then plain vector
//     differences.
//   - A moon OF the body the ship is orbiting is two impulses in that body's
//     own frame, on its own mass: `planMoonTransfer`. Nothing escapes a well
//     here - the ship stays in the parent's, so each burn costs the impulse
//     itself - and the turn into the moon's plane is made on the line where the
//     two planes cross, at whichever end is cheaper, on the conic that reaches
//     the moon's real position from there. Arriving is a capture into orbit at the moon only where the moon
//     holds one: `sphereOfInfluenceKm` under the moon's own surface (Phobos,
//     7.2 km against a radius of 11.1) means nothing orbits it and the arrival
//     is a velocity match instead. A moon of any OTHER body is refused, since
//     the plan would have to cross two frames.
//
// Units are km, km/s and km3/s2 throughout, including the exported `gmKm3S2`.
// `src/sim/flight.ts` steps this same heliocentric ecliptic J2000 frame in
// metres, m/s and m3/s2, so an autopilot flying one of these burns through it
// scales a speed by 1e3 and a GM by 1e9.
//
// Out of the model, carried in `assumptions` for the map to show: gravity
// assists, bi-elliptic and low-thrust arcs, plane changes and the inclinations
// that would need them, finite-burn loss, and any window search finer than the
// coplanar phase angle.

import { bodyById } from '../world/bodies.ts';
import { AU_KM, GM_SUN_KM3_S2, elementsAt, heliocentric, kepler } from '../world/ephemeris.ts';
import type { State, Vec3 } from '../world/ephemeris.ts';
import type { BodyRow, Orbit } from '../world/types.ts';
import { GRAVITATIONAL_CONSTANT_M3_KG_S2 } from './constants.ts';

const SECONDS_PER_DAY = 86400;
const TWO_PI = Math.PI * 2;

/** Circular orbit a transfer departs from and captures into unless the caller
 *  says otherwise. 400 km over the body's own registry radius. */
export const DEFAULT_PARKING_ALTITUDE_KM = 400;

/** Which frame a burn's cost and direction belong to. `heliocentric` is an
 *  impulse in free space, `body-relative` one made at periapsis of a conic
 *  about `referenceId`, where the cost includes that body's well. A moon
 *  transfer's departure is `body-relative` against the parent the ship is
 *  orbiting and costs the impulse itself, since it leaves nothing; its arrival
 *  is `heliocentric` where the moon holds no orbit and the burn is a velocity
 *  match. */
export type BurnFrame = 'heliocentric' | 'body-relative';

/** The burn's direction against the velocity the ship has when it makes it:
 *  its heliocentric velocity for a `heliocentric` burn, its velocity relative
 *  to `referenceId` for a `body-relative` one. A capture into orbit is always
 *  retrograde; so is a departure by a ship already carrying more speed than the
 *  transfer wants. `across` is a burn pointed within SENSE_ACROSS_DEG of square
 *  to that velocity, where neither of the other two words says anything. */
export type BurnSense = 'prograde' | 'retrograde' | 'across';

/**
 * Degrees either side of square to the velocity inside which a burn reads
 * `across` rather than by the sign of its projection.
 *
 * The rule the word obeys: `sense` is the burn's DIRECTION against the ship's
 * own motion and not what it does to the ship's speed. Off a closed orbit
 * those are different questions - the post-burn speed is escape speed whichever
 * way the impulse points, so a departure can point backwards and still leave
 * the ship faster than it found it. A departure from a point of an eccentric
 * orbit sits routinely within a degree of square, one read `retrograde` on a
 * projection cosine of -0.0038, and a word that flipped there would be
 * reporting the last bits of the mantissa.
 */
const SENSE_ACROSS_DEG = 5;

/** Where a body-relative burn is made and which way the ship thrusts there.
 *  `direction` on the burn is the heliocentric asymptote the transfer wants,
 *  which is not the direction an autopilot points the nose. */
export interface LocalBurnPoint {
  /** Body the position and velocity are measured against. */
  readonly referenceId: string;
  /** The burn point, relative to that body, km, ecliptic J2000 axes. */
  readonly positionKm: Vec3;
  /** The velocity the ship has there, relative to that body, km/s. */
  readonly velocityKmS: Vec3;
  /** Unit vector the impulse itself points along, ecliptic J2000 axes. */
  readonly thrustDirection: Vec3;
}

export type BurnKind = 'departure' | 'capture';

export interface TransferBurn {
  readonly kind: BurnKind;
  /** Short enough for a map row: 'Depart Earth', 'Capture at Mars'. */
  readonly label: string;
  /** TDB Julian date the burn is made at. */
  readonly epochJd: number;
  /** What the burn costs the ship, km/s: the impulse itself in free space, the
   *  escape or insertion burn where a body's well is in play. Where the context
   *  carries the ship's own thrust this is the finite burn at that thrust, so a
   *  pilot who cuts here is where the plan wants him. */
  readonly dvKmS: number;
  /** The same burn as a single instant push, km/s. Equal to `dvKmS` except on a
   *  departure priced at a ship's own thrust, where the difference is what the
   *  climb costs over the time the engine is open. */
  readonly impulseDvKmS: number;
  /** The heliocentric velocity change the burn produces, km/s. Equal to
   *  `dvKmS` outside a well. This is the number an autopilot applies along
   *  `direction`; `dvKmS` is the number the map spends. */
  readonly heliocentricDvKmS: number;
  readonly frame: BurnFrame;
  /** Body whose well the cost includes, or 'sol' for a free-space impulse. */
  readonly referenceId: string;
  readonly sense: BurnSense;
  /** Unit vector, ecliptic J2000 axes, the heliocentric velocity change points
   *  along at `epochJd`. For a `body-relative` burn that is the OUTGOING (or
   *  incoming) ASYMPTOTE of the escape conic and not the direction the ship
   *  thrusts: `localBurn` carries the burn point and the thrust direction. */
  readonly direction: Vec3;
  /** Where the ship is and which way it thrusts, for a burn whose cost
   *  includes a body's well and whose local orbit the plan knows. Null for a
   *  free-space impulse, where `direction` IS the thrust direction, and for a
   *  capture, whose orbit about the destination the coplanar model does not
   *  place in space. */
  readonly localBurn: LocalBurnPoint | null;
  /** Hyperbolic excess speed this burn buys or kills, km/s. */
  readonly vInfKmS: number;
  /** Semi-major axis, km, of the BOUND ellipse a departure puts the ship on
   *  about `referenceId`, where the whole transfer stays inside that body's
   *  well. Null on a capture and on a departure that leaves the well, where
   *  `vInfKmS` is a hyperbolic excess and there is no ellipse to fly. The
   *  guidance reads it to pick which departure law it is on. */
  readonly boundSemiMajorKm: number | null;
  /** A capture into orbit priced as the burn this ship makes, where the
   *  context named a thrust; absent on an instant price. */
  readonly finiteCapture?: FiniteCapture;
}

/** The finite escape from the parking circle run backwards: the arrival
 *  coasts in on a hyperbola with periapsis `periapsisKm`, the throttle opens
 *  `leadS` before that periapsis would be passed, and thrust held against the
 *  motion ends the burn on the circle at `parkingRadiusKm`. Inside
 *  `sphereKm` of the destination the guidance re-prices that periapsis at the
 *  excess speed the ship actually brings; null where the registry gives no
 *  sphere. */
export interface FiniteCapture {
  readonly periapsisKm: number;
  readonly leadS: number;
  readonly parkingRadiusKm: number;
  readonly sphereKm: number | null;
  /** The destination's GM, km3/s2, which is what says whether the drive or
   *  the pull shapes the capture. */
  readonly gmKm3S2?: number;
}

export interface TransferPlan {
  readonly destinationId: string;
  readonly destinationName: string;
  readonly burns: readonly TransferBurn[];
  /** Height over the destination's radius, km, of the orbit the capture is priced into. */
  readonly captureAltitudeKm?: number;
  /** Sum of `dvKmS` over the burns: what the trip costs the ship. */
  readonly totalDvKmS: number;
  /** Sum of `heliocentricDvKmS`: the free-space two-impulse total, which is
   *  what the registry's own coplanar derivation quotes. */
  readonly heliocentricDvKmS: number;
  readonly timeOfFlightDays: number;
  /** Excess speed out plus excess speed in for the flat two-burn transfer
   *  between circles at each body's own semi-major axis: the figure the map
   *  quoted before it priced the real orbits. */
  readonly textbookFreeDvKmS: number;
  /** That same flat transfer priced from and into the parking orbits, which is
   *  the figure the panel shows beside the real one. */
  readonly textbookTotalDvKmS: number;
  readonly textbookTimeOfFlightDays: number;
  /** TDB Julian date of the departure burn: the next window from the epoch the
   *  plan was asked for. */
  readonly departureEpochJd: number;
  readonly arrivalEpochJd: number;
  /** Days from the asked-for epoch to the departure burn. */
  readonly windowWaitDays: number;
  /** Days from this window to the next opportunity of the same kind: the true
   *  synodic period for a heliocentric leg's exact phase solve, where it is
   *  both the period and the bound `windowWaitDays` cannot cross. A moon leg's
   *  search is a plane crossing dated AND phased, which repeats on no simple
   *  two-body period, so this is instead the span the search actually covers -
   *  one full turn of the slower of the ship and the moon - which is still the
   *  bound on `windowWaitDays`, just not the classic synodic angle. */
  readonly synodicPeriodDays: number;
  /** Departure and arrival orbit radii, km, the circular ends of the ellipse. */
  readonly departureRadiusKm: number;
  readonly arrivalRadiusKm: number;
  /** Semi-major axis of the transfer ellipse, km. */
  readonly transferSemiMajorKm: number;
  /** Degrees the destination's own orbit plane sits out of the plane the ship
   *  flies this transfer in, or null where the trip has no second plane to be
   *  out of. The geometry only: what the turn through it costs is
   *  `planeChangeDvKmS`. */
  readonly planeTiltDeg: number | null;
  /** Which burn pays the turn through `planeTiltDeg` - the end the node
   *  search found cheapest - or null where there is no second plane to turn
   *  into. */
  readonly planeChangeEnd: 'departure' | 'arrival' | null;
  /** What that turn adds to `totalDvKmS` over the same two pushes flown flat,
   *  which is the difference between `totalDvKmS` and `textbookTotalDvKmS`.
   *  For a departure-end Lambert leg this is not the turn alone: it also
   *  carries the path and phasing difference from the flat Hohmann. */
  readonly planeChangeDvKmS: number;
}

export interface TransferSkip {
  readonly id: string;
  /** The row's own display name, so a map can name it rather than print an id. */
  readonly name: string;
  readonly reason: string;
}

/** Which point of the ship's own orbit every departure in a survey is priced
 *  at. `circular-point` is a circular orbit, whose departure point is free and
 *  which the plan names; `cheapest-on-orbit` the cheapest point of one
 *  revolution of a closed orbit that clears the surface, which the ship comes
 *  round to before the window; `ship-point` the point the ship is at, on a
 *  conic that never brings it round again; `free-space` an impulse with no
 *  well to climb. */
export type DeparturePricing =
  | 'circular-point'
  | 'cheapest-on-orbit'
  | 'ship-point'
  | 'free-space';

export interface TransferSurvey {
  /** The epoch the survey was planned from, TDB Julian date. */
  readonly epochJd: number;
  /** Body the ship departs from, or null in deep space. */
  readonly originBodyId: string | null;
  readonly originLabel: string;
  /** Body whose sphere of influence the ship is actually inside, which is the
   *  moon rather than the planet the plan departs from. Equal to `originLabel`
   *  everywhere else. */
  readonly hostLabel: string;
  /** Heliocentric radius the transfers leave from, km. */
  readonly originRadiusKm: number;
  /** Which question every departure figure in this survey answers. */
  readonly departurePricing: DeparturePricing;
  /** Cheapest `totalDvKmS` first. */
  readonly plans: readonly TransferPlan[];
  readonly skipped: readonly TransferSkip[];
  /** What the model leaves out, for the map to show beside its numbers. */
  readonly assumptions: readonly string[];
}

/** Where the ship is planning from: a circular parking orbit at a body, or its
 *  own live heliocentric position and velocity. */
export type TransferOrigin =
  | { readonly kind: 'parking'; readonly bodyId: string; readonly altitudeKm?: number }
  | { readonly kind: 'state'; readonly state: State };

export interface TransferContext {
  readonly rows: readonly BodyRow[];
  /** TDB Julian date the plan is asked for. */
  readonly epochJd: number;
  /** Circular orbit the transfer captures into at the destination, and the
   *  default departure orbit. Defaults to DEFAULT_PARKING_ALTITUDE_KM. */
  readonly parkingAltitudeKm?: number;
  /** Height over each destination's radius, km, its capture settles into where
   *  that is not `parkingAltitudeKm`: the arrival orbit picked for GO. */
  readonly captureAltitudeKm?: (destination: BodyRow) => number;
  /**
   * Price every departure at the point the ship is at, even on a closed orbit
   * it comes round to a cheaper point of before the window.
   *
   * The default is the rule below and this is the other question: what one
   * impulse made now, where the ship is, would cost. `dvmap-check.mjs` reads
   * its point-by-point figures with it, and `?dvmapburnnow=1` puts the panel
   * back to it so `--dvmap`'s closed-orbit bars have a control that fails
   * them. Nothing a player reaches sets it.
   */
  readonly departureAtShipPoint?: boolean;
  /**
   * Take a moon's own orbit plane off its J2000 element fit rather than off
   * this epoch, which is where the out-of-plane refusal first read it.
   *
   * Phobos's plane moves 1.1 degrees between J2000 and the game's own boot
   * date, so both the tilt a planned row prints and the degrees a refusal
   * names come out wrong under it. `dvmap-check.mjs --inject plane-j2000` and
   * `?dvmapplanej2000=1` are the two ways in. Nothing a player reaches sets it.
   */
  readonly planeFromJ2000?: boolean;
  /**
   * Grid, refinement and time-of-flight range every window search in this
   * survey runs at, for the two controls that have to knock the prices over:
   * a grid with no refinement lands on a grid line rather than on a minimum,
   * and lifting the time-of-flight cap quotes the outer system a coast no
   * player would fly. Nothing a player reaches sets it.
   */
  readonly windowSearch?: WindowSearchOptions;
  /**
   * Route a ship free of every sphere through the flat model instead of the
   * real-orbit search, the way every heliocentric row priced before that fix.
   * `dvmap-check.mjs --inject deep-space-flat` is the one way in. Nothing a
   * player reaches sets it.
   */
  readonly forceFlatDeepSpace?: boolean;
  /**
   * The thrust the ship actually has, m/s2, which prices the departure as the
   * burn it really is rather than as an instant push.
   *
   * Left out, every burn is an impulse and `dvKmS` is that impulse, which is
   * what `dvmap-check.mjs` and the registry's own derivations read. Given, the
   * departure's `dvKmS` becomes what this ship spends climbing out at that
   * thrust and `impulseDvKmS` keeps the instant-push figure beside it. The map
   * hands the ship's own drive in, so the figure a pilot cuts on is the one
   * that leaves the ship free of the body.
   */
  readonly shipAccelMps2?: number;
  /**
   * Plan a moon trip the way the planner did before the turn was put on the
   * line of nodes: the window off the moon's angle projected into the ship's
   * plane, and the departure turned about the burn radius, which quotes a trip
   * the ship cannot fly for that price. `transit-check.mjs --inject
   * turn-about-radius` is the one way in. Nothing a player reaches sets it.
   */
  readonly turnAboutBurnRadius?: boolean;
  /**
   * A moon leg's node-crossing search still runs its full span, but the plan
   * it returns claims a `synodicPeriodDays` a twentieth as wide - so a window
   * the search genuinely found past the claimed span is the control that
   * proves the gate barring `windowWaitDays` to that span can fail.
   * `dvmap-check.mjs --inject window-span-lie` is the one way in. Nothing a
   * player reaches sets it.
   */
  readonly lieAboutWindowSpan?: boolean;
  /**
   * A moon leg searches only the end whose closed-form turn is cheaper, unless
   * the other is within a quarter of it, which quotes an eccentric orbit's trip
   * up to half again over the one the other end has. `dvmap-check.mjs --inject
   * one-end` is the one way in. Nothing a player reaches sets it.
   */
  readonly cheaperEndOnly?: boolean;
  /**
   * A moon leg that turns as it leaves is solved only the way round the ship's
   * own orbit runs, which prices a retrograde orbit's trip off the dearer of the
   * two arcs to the same point and refuses a ship on nearly the moon's own orbit
   * the trip it has. `dvmap-check.mjs --inject ship-pole-sense` is the one way
   * in. Nothing a player reaches sets it.
   */
  readonly shipPoleSense?: boolean;
}

/**
 * The departure line, which states the pricing rule in all three cases so the
 * list is true with no survey to narrow it. A survey appends the case that
 * applied to it, from DEPARTURE_PRICING_NOTES.
 */
const DEPARTURE_ASSUMPTION =
  'A departure is the impulse onto the conic that leaves along the transfer\'s asymptote, the cheaper of the two ways round the body that clear its surface. A circular orbit has no point the ship must leave from, so the plan names the one whose conic comes out along the asymptote; any other closed orbit clearing the surface is priced at the cheapest point of one revolution, since the ship comes round to every point of it long before the window; a conic that never brings it round is priced where the ship is.';

/** What the departure line gains once a survey knows which case it priced. */
const DEPARTURE_PRICING_NOTES: Readonly<Record<DeparturePricing, string>> = {
  'circular-point': ' This orbit is circular, so the point is the plan\'s to choose and it names it.',
  'cheapest-on-orbit': ' This orbit is closed and clears the surface, so the price is its cheapest point and not the point the ship is at now.',
  'ship-point': ' This conic does not bring the ship round again, so the price is the point it is at now.',
  'free-space': ' Nothing is pulling here, so a departure costs the impulse itself.',
};

/** The one assumption line a survey rewrites: inside a moon's sphere of
 *  influence it names what escaping the moon would have cost. */
const MOON_ESCAPE_ASSUMPTION =
  'A ship inside a moon\'s sphere of influence plans from the moon\'s parent. Escaping the moon itself is not priced.';

/** The two lines a moon of the body the ship is orbiting adds to the model. */
const MOON_TRANSFER_ASSUMPTION =
  'A moon of the body the ship is orbiting is planned in that body\'s own frame and on its own mass. The turn out of the ship\'s plane into the moon\'s is made where the two planes cross: the ship leaves as its own orbit crosses the moon\'s plane, or arrives as the moon crosses the ship\'s, whichever is cheaper. The trip is the path that reaches the moon\'s real position from there, and the ship waits up to one turn of the slower of its own orbit and the moon\'s for the cheapest.';
/** The burn-length line, and what it becomes once the context names a thrust:
 *  the departure and the capture into orbit are then the burns the ship really
 *  makes. */
const IMPULSE_ASSUMPTION =
  'Instant pushes. Nothing is charged for the time the engine is open at the thrust the ship actually has.';
const FINITE_DEPARTURE_ASSUMPTION =
  'Leaving and braking into orbit at the far end are the burns your own engine makes, so their figures carry what the climb and the descent cost over the time the engine is open; a stronger engine brings both down toward the instant figure. A trip to a moon of the world you are orbiting, which never leaves its pull, is still priced as instant pushes.';

const MOON_MATCH_ASSUMPTION =
  'A moon whose own pull holds nothing above its own surface has no orbit around it to capture into, so the trip ends on the moon\'s own orbit about its planet a short way from it, where the ship stays near the moon instead of drifting off.';

/**
 * Longest flight the window search will look at, as a multiple of the flat
 * transfer's own time of flight.
 *
 * A range that suits Mars quotes the outer system a trip no player would fly:
 * with no cap the cheapest Earth to Pluto window is a 100 year coast, and
 * Neptune's is 86 years against the 21 the cap leaves. One number, and the
 * whole search's upper bound.
 */
export const WINDOW_TOF_CAP_FRACTION = 1.8;

export const TRANSFER_ASSUMPTIONS: readonly string[] = [
  'Both orbits are the real ones at the date of the burn, tilt and all: the trip is the single-revolution conic through the two positions and the Sun.',
  'Two pushes. No gravity assist, no bi-elliptic or low-thrust arc.',
  'The turn out of the ecliptic is paid for, at the departure, where it is cheapest.',
  IMPULSE_ASSUMPTION,
  'The window is the cheapest departure date and flight time found on a grid over one span between windows, refined round its best cell.',
  `No flight longer than ${WINDOW_TOF_CAP_FRACTION} times the flat transfer's own is searched, so a slower and cheaper trip to the outer system is not offered.`,
  'Capture is into a circular parking orbit at the destination, not a flyby.',
  MOON_TRANSFER_ASSUMPTION,
  MOON_MATCH_ASSUMPTION,
  MOON_ESCAPE_ASSUMPTION,
  DEPARTURE_ASSUMPTION,
  'A ship outside every sphere of influence has no well to climb: the departure is the plain difference between the ship\'s own velocity and the one the transfer needs, and the ship is carried to its window on its own real orbit around the Sun.',
];

/** The panel's fixed two-line digest of `TRANSFER_ASSUMPTIONS` above, same
 *  for every survey; the list stays the single source, this is not a narrower model. */
export const TRANSFER_ASSUMPTIONS_SUMMARY: readonly string[] = [
  'Rough figures: two pushes, real tilted orbits.',
  'No slingshots, no spirals, your own thrust.',
];

const ZERO_VEC: Vec3 = { x: 0, y: 0, z: 0 };

/** How near circular a local orbit reads before the plan treats its departure
 *  point as free to choose. */
const CIRCULAR_TOLERANCE = 1e-9;

function norm(v: Vec3): number {
  return Math.hypot(v.x, v.y, v.z);
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

function scale(v: Vec3, k: number): Vec3 {
  return { x: v.x * k, y: v.y * k, z: v.z * k };
}

function unit(v: Vec3): Vec3 {
  const n = norm(v);
  return n === 0 ? ZERO_VEC : scale(v, 1 / n);
}

function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

/** Rodrigues, about a unit axis. */
function rotateAbout(v: Vec3, axis: Vec3, angleRad: number): Vec3 {
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  const k = dot(axis, v) * (1 - c);
  const perp = cross(axis, v);
  return {
    x: v.x * c + perp.x * s + axis.x * k,
    y: v.y * c + perp.y * s + axis.y * k,
    z: v.z * c + perp.z * s + axis.z * k,
  };
}

/** In-plane prograde direction at a heliocentric position: the ecliptic pole
 *  crossed into the radial unit vector. */
function progradeAt(position: Vec3): Vec3 {
  const r = Math.hypot(position.x, position.y);
  if (r === 0) return ZERO_VEC;
  return { x: -position.y / r, y: position.x / r, z: 0 };
}

/** What the impulse does to the ship's speed, in one word. A ship at rest has
 *  no direction to be measured against and reads prograde, which is what a burn
 *  from nothing to something is. */
function senseOf(impulse: Vec3, velocityKmS: Vec3): BurnSense {
  const dv = norm(impulse);
  const speed = norm(velocityKmS);
  if (dv <= 0 || speed <= 0) return 'prograde';
  const cosine = dot(impulse, velocityKmS) / (dv * speed);
  if (Math.abs(cosine) < Math.cos(((90 - SENSE_ACROSS_DEG) * Math.PI) / 180)) return 'across';
  return cosine >= 0 ? 'prograde' : 'retrograde';
}

function wrapTwoPi(angleRad: number): number {
  const w = angleRad % TWO_PI;
  return w < 0 ? w + TWO_PI : w;
}

/** Gravitational parameter, km^3/s^2, from the row's own `mass_kg` and nothing
 *  else - which is what keeps the map and the flight model agreeing. */
export function gmKm3S2(row: BodyRow): number {
  return (GRAVITATIONAL_CONSTANT_M3_KG_S2 * row.mass_kg) / 1e9;
}

/** Height over `destination`'s radius, km, a capture there is priced into. */
export function captureAltitudeKmFor(destination: BodyRow, ctx: TransferContext): number {
  return ctx.captureAltitudeKm?.(destination) ?? ctx.parkingAltitudeKm ?? DEFAULT_PARKING_ALTITUDE_KM;
}

/** Radius of the body's sphere of influence about its parent, km, or null for
 *  a row with no orbit or no parent in the registry. */
export function sphereOfInfluenceKm(row: BodyRow, rows: readonly BodyRow[]): number | null {
  const orbit = row.orbit;
  if (orbit === null || row.parent === null) return null;
  const parent = bodyById(rows, row.parent);
  if (parent === undefined || parent.mass_kg <= 0) return null;
  return orbit.a_au * AU_KM * Math.pow(row.mass_kg / parent.mass_kg, 0.4);
}

/**
 * The registry row whose sphere of influence holds this heliocentric position,
 * innermost first, or null out in the open where only the Sun pulls. The
 * innermost wins so a ship in low Earth orbit answers Earth rather than
 * whatever larger sphere also contains it.
 */
export function dominantBodyAt(positionKm: Vec3, ctx: TransferContext): BodyRow | null {
  let best: BodyRow | null = null;
  let bestSoi = Infinity;
  for (const row of ctx.rows) {
    if (row.orbit === null) continue;
    const soi = sphereOfInfluenceKm(row, ctx.rows);
    if (soi === null) continue;
    const centre = heliocentric(row, ctx.rows, ctx.epochJd).position;
    if (norm(sub(positionKm, centre)) > soi) continue;
    if (soi < bestSoi) {
      best = row;
      bestSoi = soi;
    }
  }
  return best;
}

/**
 * Heliocentric state of a ship on a circular parking orbit at a body, for a
 * caller that wants to hand `planTransfers` a live state vector that stands
 * for the parking orbit rather than naming the body. The orbit is taken in the
 * ecliptic plane, prograde, with the ship at the point trailing the body along
 * its own heliocentric radial - any point does, since a circular orbit's own
 * departure point is free.
 */
export function parkingOrbitState(
  bodyId: string,
  ctx: TransferContext,
  altitudeKm?: number,
): State | null {
  const row = bodyById(ctx.rows, bodyId);
  if (row === undefined) return null;
  const radiusKm = row.radius_m / 1000 + (altitudeKm ?? ctx.parkingAltitudeKm ?? DEFAULT_PARKING_ALTITUDE_KM);
  const gm = gmKm3S2(row);
  if (radiusKm <= 0 || gm <= 0) return null;
  const centre = heliocentric(row, ctx.rows, ctx.epochJd);
  const outward = unit({ x: centre.position.x, y: centre.position.y, z: 0 });
  const along = progradeAt(centre.position);
  const speed = Math.sqrt(gm / radiusKm);
  return {
    position: {
      x: centre.position.x + outward.x * radiusKm,
      y: centre.position.y + outward.y * radiusKm,
      z: centre.position.z + outward.z * radiusKm,
    },
    velocity: {
      x: centre.velocity.x + along.x * speed,
      y: centre.velocity.y + along.y * speed,
      z: centre.velocity.z + along.z * speed,
    },
  };
}

/** What the heliocentric side of a transfer departs from, once the origin has
 *  been resolved to either a body or a free-flying state. */
interface Departure {
  readonly bodyId: string | null;
  readonly label: string;
  /** Sphere of influence the ship is really inside, which is the moon where
   *  `label` is its planet. */
  readonly hostLabel: string;
  /** That host's own id, so a plan can tell a moon the ship is sitting at from
   *  one it has to fly to. */
  readonly hostId: string | null;
  /** What escaping that moon would cost from where the ship is, km/s, or null
   *  where the host IS the body the plan departs from and nothing is left out. */
  readonly hostEscapeKmS: number | null;
  /** Heliocentric radius the ellipse leaves from, km. */
  readonly radiusKm: number;
  /** Where the ship is while it waits for the window. A body-bound ship reads
   *  its body's own row; a free-flying one is carried forward on its own real
   *  conic about the Sun (`stateFromWell`), or the fixed circular rate below
   *  where that conic is not closed. */
  readonly positionAt: (epochJd: number) => Vec3;
  readonly velocityAt: (epochJd: number) => Vec3;
  /** Mean motion of the orbit the ship coasts on while it waits, rad/s. */
  readonly meanMotionRadPerS: number;
  /** Well the departure burn has to climb, or null in free space. */
  readonly well: DepartureWell | null;
}

/** The ship's own state inside a body's well, which is what the departure burn
 *  is priced against. */
interface DepartureWell {
  readonly gm: number;
  readonly radiusKm: number;
  readonly speedKmS: number;
  /** Position and velocity relative to the body, km and km/s, ecliptic J2000
   *  axes. */
  readonly positionKm: Vec3;
  readonly velocityKmS: Vec3;
  /** The body's own surface, km from its centre: an escape conic that dips
   *  below it is not flyable and the plan refuses it. */
  readonly surfaceRadiusKm: number;
  /** A circular orbit has no point the ship has to leave from, so the plan
   *  names the point that puts the asymptote where the transfer wants it, in
   *  closed form and to the last digit of the parked figure. */
  readonly circular: boolean;
  /** The ship's own orbit about the body where that orbit is closed and clears
   *  the surface, which is what makes every point of it reachable before the
   *  window. Null on an open conic and on one that meets the ground. */
  readonly orbit: ClosedOrbit | null;
}

/** A closed orbit the ship flies round, as the elements one revolution is
 *  sampled from. */
interface ClosedOrbit {
  readonly semiLatusKm: number;
  readonly eccentricity: number;
  /** Unit vectors at true anomaly 0 and 90, in the orbit's own plane. */
  readonly periapsisDir: Vec3;
  readonly transverseDir: Vec3;
}

/** The ship's orbit about the body, or null where it is open, degenerate, or
 *  passes under the surface - the three cases the ship never comes round on. */
function closedOrbitOf(
  gm: number,
  positionKm: Vec3,
  velocityKmS: Vec3,
  surfaceRadiusKm: number,
): ClosedOrbit | null {
  const radiusKm = norm(positionKm);
  const speedKmS = norm(velocityKmS);
  if (gm <= 0 || radiusKm <= 0) return null;
  if ((speedKmS * speedKmS) / 2 - gm / radiusKm >= 0) return null;
  const momentum = cross(positionKm, velocityKmS);
  const momentumKm2S = norm(momentum);
  if (momentumKm2S <= 0) return null;
  const semiLatusKm = (momentumKm2S * momentumKm2S) / gm;
  const eVec = scale(
    sub(
      scale(positionKm, speedKmS * speedKmS - gm / radiusKm),
      scale(velocityKmS, dot(positionKm, velocityKmS)),
    ),
    1 / gm,
  );
  const eccentricity = norm(eVec);
  if (eccentricity >= 1) return null;
  if (semiLatusKm / (1 + eccentricity) < surfaceRadiusKm) return null;
  const periapsisDir = eccentricity > 1e-12 ? unit(eVec) : unit(positionKm);
  return {
    semiLatusKm,
    eccentricity,
    periapsisDir,
    transverseDir: unit(cross(unit(momentum), periapsisDir)),
  };
}

/** The first row up the parent chain that orbits the Sun itself, which is the
 *  only kind of row whose `a_au` is a heliocentric radius. Null where the chain
 *  leaves the registry or loops. */
function heliocentricAnchor(row: BodyRow, rows: readonly BodyRow[]): BodyRow | null {
  let current: BodyRow | undefined = row;
  for (let hop = 0; hop < 8; hop++) {
    if (current === undefined || current.orbit === null) return null;
    if (!current.orbit.parent_relative) return current;
    if (current.parent === null) return null;
    current = bodyById(rows, current.parent);
  }
  return null;
}

function resolveDeparture(origin: TransferOrigin, ctx: TransferContext): Departure | string {
  if (origin.kind === 'parking') {
    const row = bodyById(ctx.rows, origin.bodyId);
    if (row === undefined) return `no registry row "${origin.bodyId}"`;
    if (row.orbit === null) return `"${origin.bodyId}" has no orbit to depart from`;
    const parkRadiusKm =
      row.radius_m / 1000 +
      (origin.altitudeKm ?? ctx.parkingAltitudeKm ?? DEFAULT_PARKING_ALTITUDE_KM);
    const gm = gmKm3S2(row);
    if (parkRadiusKm <= 0 || gm <= 0) return `"${origin.bodyId}" carries no radius or mass to park at`;
    // The same orbit `parkingOrbitState` hands out: in the ecliptic plane,
    // prograde, at the point trailing the body along its own solar radial.
    const centre = heliocentric(row, ctx.rows, ctx.epochJd);
    const outward = unit({ x: centre.position.x, y: centre.position.y, z: 0 });
    const along = progradeAt(centre.position);
    return departureAtBody(
      row,
      ctx,
      gm,
      scale(outward, parkRadiusKm),
      scale(along, Math.sqrt(gm / parkRadiusKm)),
      row.display_name,
      row.id,
      null,
    );
  }
  const position = origin.state.position;
  const velocity = origin.state.velocity;
  const host = dominantBodyAt(position, ctx);
  if (host !== null && host.orbit !== null) {
    // A moon's a_au is measured against its planet, so it is not a heliocentric
    // radius and the transfer leaves from the planet's orbit, not the moon's.
    const anchor = heliocentricAnchor(host, ctx.rows);
    if (anchor === null) {
      return `"${host.id}" orbits a body the registry does not carry a solar orbit for`;
    }
    const centre = heliocentric(anchor, ctx.rows, ctx.epochJd);
    const relativePositionKm = sub(position, centre.position);
    const gm = gmKm3S2(anchor);
    if (norm(relativePositionKm) <= 0) return `the ship is at "${anchor.id}"'s own centre`;
    if (gm <= 0) return `"${anchor.id}" carries no mass to escape`;
    return departureAtBody(
      anchor,
      ctx,
      gm,
      relativePositionKm,
      sub(velocity, centre.velocity),
      host.display_name,
      host.id,
      host.id === anchor.id ? null : escapeFromHostKmS(host, origin.state, ctx),
    );
  }
  const radiusKm = Math.hypot(position.x, position.y, position.z);
  if (radiusKm <= 0) return 'the ship is at the frame origin';
  const meanMotionRadPerS = Math.sqrt(GM_SUN_KM3_S2 / (radiusKm * radiusKm * radiusKm));
  // The ship's own conic about the Sun, read the same way a body-relative well
  // is: `wellAfter` carries it to any epoch the window search asks about.
  const heliocentricOrbit = closedOrbitOf(GM_SUN_KM3_S2, position, velocity, 0);
  if (heliocentricOrbit === null) {
    return 'faster than solar escape speed while free of every sphere of influence: outside this model';
  }
  const stateAt = (epochJd: number): State =>
    stateFromWell(
      {
        gm: GM_SUN_KM3_S2,
        radiusKm,
        speedKmS: norm(velocity),
        positionKm: position,
        velocityKmS: velocity,
        surfaceRadiusKm: 0,
        circular: false,
        orbit: heliocentricOrbit,
      },
      heliocentricOrbit,
      ctx.epochJd,
      epochJd,
    );
  return {
    bodyId: null,
    label: 'deep space',
    hostLabel: 'deep space',
    hostId: null,
    hostEscapeKmS: null,
    radiusKm,
    positionAt: (epochJd) => stateAt(epochJd).position,
    velocityAt: (epochJd) => stateAt(epochJd).velocity,
    meanMotionRadPerS,
    well: null,
  };
}

/** What the plan leaves out when it departs from a moon's parent: the speed the
 *  ship still owes to climb out of the moon's own well from where it sits. */
function escapeFromHostKmS(host: BodyRow, state: State, ctx: TransferContext): number | null {
  const gm = gmKm3S2(host);
  const centre = heliocentric(host, ctx.rows, ctx.epochJd);
  const radiusKm = norm(sub(state.position, centre.position));
  if (gm <= 0 || radiusKm <= 0) return null;
  return Math.sqrt((2 * gm) / radiusKm) - norm(sub(state.velocity, centre.velocity));
}

/** A departure from inside a body's sphere of influence: the heliocentric side
 *  is the body's own circular orbit, the cost side the ship's live local
 *  radius and speed. */
function departureAtBody(
  row: BodyRow,
  ctx: TransferContext,
  gm: number,
  localPositionKm: Vec3,
  localVelocityKmS: Vec3,
  hostLabel: string,
  hostId: string,
  hostEscapeKmS: number | null,
): Departure {
  const orbit = row.orbit;
  const radiusKm = orbit === null ? 0 : orbit.a_au * AU_KM;
  const localRadiusKm = norm(localPositionKm);
  const localSpeedKmS = norm(localVelocityKmS);
  const radialSpeedKmS =
    localRadiusKm === 0 ? 0 : dot(localVelocityKmS, localPositionKm) / localRadiusKm;
  const circularSpeedKmS = localRadiusKm > 0 ? Math.sqrt(gm / localRadiusKm) : 0;
  const circular =
    localSpeedKmS > 0 &&
    circularSpeedKmS > 0 &&
    Math.abs(radialSpeedKmS) / localSpeedKmS < CIRCULAR_TOLERANCE &&
    Math.abs(localSpeedKmS - circularSpeedKmS) / circularSpeedKmS < CIRCULAR_TOLERANCE;
  return {
    bodyId: row.id,
    label: row.display_name,
    hostLabel,
    hostId,
    hostEscapeKmS,
    radiusKm,
    positionAt: (epochJd) => heliocentric(row, ctx.rows, epochJd).position,
    velocityAt: (epochJd) => heliocentric(row, ctx.rows, epochJd).velocity,
    meanMotionRadPerS: Math.sqrt(GM_SUN_KM3_S2 / (radiusKm * radiusKm * radiusKm)),
    well: {
      gm,
      radiusKm: localRadiusKm,
      speedKmS: localSpeedKmS,
      positionKm: localPositionKm,
      velocityKmS: localVelocityKmS,
      surfaceRadiusKm: row.radius_m / 1000,
      circular,
      orbit: closedOrbitOf(gm, localPositionKm, localVelocityKmS, row.radius_m / 1000),
    },
  };
}

function wrapPi(angleRad: number): number {
  const w = wrapTwoPi(angleRad);
  return w > Math.PI ? w - TWO_PI : w;
}

/**
 * Departure epoch, in days from `ctx.epochJd`, at which the destination leads
 * the ship by exactly the angle it sweeps while the ship is on the ellipse.
 *
 * The first guess advances both bodies at their mean motions; the refinement
 * then reads the phase off the ephemeris itself and Newtons on it with a
 * numerical derivative, so the epoch the plan names is a window against the
 * positions the world actually draws rather than against a circular fiction.
 * A pair whose phase does not open (equal mean motions) never gets this far.
 *
 * `phaseAt` is how far the destination leads the ship at an epoch, which is a
 * heliocentric longitude for a trip between planets and an angle in the ship's
 * own orbit plane about the parent for a trip to a moon. The derivative's step
 * scales with the synodic period for the same reason: a moon's window comes
 * round in hours, where the half-day step a planet's uses would step over it.
 */
function solveWindowDays(
  phaseAt: (epochJd: number) => number,
  ctx: TransferContext,
  wantedPhaseRad: number,
  rateRadPerS: number,
): number {
  const synodicSeconds = TWO_PI / Math.abs(rateRadPerS);
  const hDays = Math.min(0.5, synodicSeconds / SECONDS_PER_DAY / 100);
  // Already at the window: leave now rather than wait a whole synodic period
  // for the next one. The bound is well inside the refinement's own residual,
  // so it catches a plan re-asked at its own departure epoch and nothing else.
  if (Math.abs(wrapPi(phaseAt(ctx.epochJd) - wantedPhaseRad)) < 1e-8) return 0;
  const gap = wantedPhaseRad - phaseAt(ctx.epochJd);
  let waitSeconds =
    rateRadPerS > 0 ? wrapTwoPi(gap) / rateRadPerS : wrapTwoPi(-gap) / -rateRadPerS;
  for (let attempt = 0; attempt < 2; attempt++) {
    for (let i = 0; i < 24; i++) {
      const jd = ctx.epochJd + waitSeconds / SECONDS_PER_DAY;
      const residual = wrapPi(phaseAt(jd) - wantedPhaseRad);
      const slope =
        wrapPi(phaseAt(jd + hDays) - phaseAt(jd - hDays)) / (2 * hDays * SECONDS_PER_DAY);
      if (slope === 0) break;
      const step = -residual / slope;
      waitSeconds += step;
      if (Math.abs(step) < 1e-4) break;
    }
    if (waitSeconds >= 0) break;
    waitSeconds += synodicSeconds;
  }
  return waitSeconds / SECONDS_PER_DAY;
}

/** Escape or insertion burn at a body: the speed a conic with this excess
 *  needs at this radius, against the speed the ship has there. SIGNED -
 *  negative is a ship that has to shed speed, and the caller reads the sign as
 *  the burn's sense. */
function wellBurnKmS(gm: number, radiusKm: number, speedKmS: number, vInfKmS: number): number {
  return Math.sqrt(vInfKmS * vInfKmS + (2 * gm) / radiusKm) - speedKmS;
}

/** Step the finite departure is integrated at, seconds, and the longest burn it
 *  will follow before it gives up and leaves the impulse standing. Half a second
 *  is a ten-thousandth of the shortest orbit a departure is priced from, and the
 *  longest departure in the registry is under ten hours at cruise thrust. */
const FINITE_BURN_STEP_S = 0.5;
const FINITE_BURN_CAP_S = 4e5;

/**
 * What the climb out of a well costs a ship that can only push so hard, km/s.
 *
 * An impulse is a push with no duration, so it pays nothing for the time the
 * ship spends deep in the well with the engine open. This integrates the burn
 * the ship really makes - two-body motion in the plane of its own position and
 * velocity, the thrust held along that velocity, semi-implicit Euler - and
 * returns the delta-v spent when the energy first reaches the transfer's own
 * excess speed. Thrust along the velocity adds energy fastest at each instant,
 * so this is what a prograde climb costs; a law that also has an asymptote to
 * hit pays more.
 *
 * Null where there is no well, no thrust, or the burn runs past the cap.
 */
export function finiteEscapeDvKmS(
  gm: number,
  positionKm: Vec3,
  velocityKmS: Vec3,
  accelKmS2: number,
  vInfKmS: number,
): number | null {
  return finiteEscapeEnd(gm, positionKm, velocityKmS, accelKmS2, vInfKmS)?.dvKmS ?? null;
}

/** Where the prograde climb `finiteEscapeDvKmS` integrates leaves the ship. */
export interface FiniteEscapeEnd {
  readonly dvKmS: number;
  /** Relative to the body, km and km/s, in the axes the start was given in. */
  readonly positionKm: Vec3;
  readonly velocityKmS: Vec3;
}

/** The same climb, with the state it ends on: the one integration both the
 *  price and the guidance's timing of the departure read. The guidance runs it
 *  inside a frame, several times over, at a coarser `stepS`. */
export function finiteEscapeEnd(
  gm: number,
  positionKm: Vec3,
  velocityKmS: Vec3,
  accelKmS2: number,
  vInfKmS: number,
  stepS: number = FINITE_BURN_STEP_S,
): FiniteEscapeEnd | null {
  if (!(stepS > 0)) return null;
  if (!(gm > 0) || !(accelKmS2 > 0)) return null;
  const radiusKm = norm(positionKm);
  if (!(radiusKm > 0)) return null;
  const outward = unit(positionKm);
  const radialKmS = dot(velocityKmS, outward);
  const transverseVec = sub(velocityKmS, scale(outward, radialKmS));
  const transverseKmS = norm(transverseVec);
  const across = transverseKmS > 0 ? scale(transverseVec, 1 / transverseKmS) : ZERO_VEC;
  const targetEnergy = (vInfKmS * vInfKmS) / 2;
  let x = radiusKm;
  let y = 0;
  let vx = radialKmS;
  let vy = transverseKmS;
  for (let t = 0; t < FINITE_BURN_CAP_S; t += stepS) {
    const r = Math.hypot(x, y);
    const v = Math.hypot(vx, vy);
    if (!(r > 0) || !(v > 0)) return null;
    if ((v * v) / 2 - gm / r >= targetEnergy) {
      return {
        dvKmS: accelKmS2 * t,
        positionKm: add(scale(outward, x), scale(across, y)),
        velocityKmS: add(scale(outward, vx), scale(across, vy)),
      };
    }
    const g = -gm / (r * r * r);
    vx += (g * x + (accelKmS2 * vx) / v) * stepS;
    vy += (g * y + (accelKmS2 * vy) / v) * stepS;
    x += vx * stepS;
    y += vy * stepS;
  }
  return null;
}

/** The departure burn with `dvKmS` moved off the instant push and onto the burn
 *  this ship actually makes, where the context named a thrust and the burn has a
 *  well to climb. Every other case comes back untouched. */
function pricedDeparture(burn: TransferBurn, ctx: TransferContext): TransferBurn {
  const accelMps2 = ctx.shipAccelMps2;
  const local = burn.localBurn;
  if (accelMps2 === undefined || !(accelMps2 > 0) || local === null) return burn;
  const host = bodyById(ctx.rows, local.referenceId);
  if (host === undefined) return burn;
  const spent = finiteEscapeDvKmS(
    gmKm3S2(host), local.positionKm, local.velocityKmS, accelMps2 / 1000, burn.vInfKmS,
  );
  return spent === null || spent <= burn.dvKmS ? burn : { ...burn, dvKmS: spent };
}

/**
 * The capture into a circle of `parkingRadiusKm` a ship that can only push so
 * hard makes, as the finite escape from that circle flown backwards: a
 * prograde climb run in reverse is thrust against the motion from the
 * hyperbola the climb ends on down to the circle it started from. Returns the
 * delta-v, that hyperbola's periapsis, and how long before that periapsis the
 * burn opens. Null where there is no well, no thrust, or the burn runs past
 * the cap.
 */
export function finiteCaptureOf(
  gm: number,
  parkingRadiusKm: number,
  accelKmS2: number,
  vInfKmS: number,
  memo?: FiniteCaptureMemo,
): { readonly dvKmS: number; readonly capture: FiniteCapture } | null {
  if (!(gm > 0) || !(accelKmS2 > 0) || !(parkingRadiusKm > 0) || !(vInfKmS > 0)) return null;
  const targetEnergy = (vInfKmS * vInfKmS) / 2;
  if (memo !== undefined) {
    const track = climbTrackFor(memo, gm, parkingRadiusKm, accelKmS2);
    const k = climbTo(track, targetEnergy);
    return k < 0 ? null : captureAtStep(track, k);
  }
  const s: ClimbState = { x: parkingRadiusKm, y: 0, vx: 0, vy: Math.sqrt(gm / parkingRadiusKm) };
  for (let t = 0; t < FINITE_BURN_CAP_S; t += FINITE_BURN_STEP_S) {
    const r = Math.hypot(s.x, s.y);
    const v = Math.hypot(s.vx, s.vy);
    if (!(r > 0) || !(v > 0)) return null;
    const energy = (v * v) / 2 - gm / r;
    if (energy >= targetEnergy) return captureFrom(s, r, energy, t, gm, parkingRadiusKm, accelKmS2);
    climbStep(s, gm, accelKmS2, r, v);
  }
  return null;
}

/** The reversed climb's state about the body, km and km/s, in the burn's own plane. */
interface ClimbState { x: number; y: number; vx: number; vy: number }

/** How many `climbStep` calls have run, across every caller; a proxy for the
 *  work a capture pricing does that does not depend on the clock. */
export const climbStepMeter = { steps: 0 };

function climbStep(s: ClimbState, gm: number, accelKmS2: number, r: number, v: number): void {
  climbStepMeter.steps++;
  const g = -gm / (r * r * r);
  s.vx += (g * s.x + (accelKmS2 * s.vx) / v) * FINITE_BURN_STEP_S;
  s.vy += (g * s.y + (accelKmS2 * s.vy) / v) * FINITE_BURN_STEP_S;
  s.x += s.vx * FINITE_BURN_STEP_S;
  s.y += s.vy * FINITE_BURN_STEP_S;
}

function captureFrom(
  s: ClimbState, r: number, energy: number, t: number,
  gm: number, parkingRadiusKm: number, accelKmS2: number,
): { readonly dvKmS: number; readonly capture: FiniteCapture } {
  const h = s.x * s.vy - s.y * s.vx;
  const e = Math.sqrt(1 + (2 * energy * h * h) / (gm * gm));
  const p = (h * h) / gm;
  const outward = s.x * s.vx + s.y * s.vy >= 0 ? 1 : -1;
  const nu = outward * Math.acos(clampUnit((p / r - 1) / e));
  const f = 2 * Math.atanh(Math.sqrt((e - 1) / (e + 1)) * Math.tan(nu / 2));
  const a = gm / (2 * energy);
  const sincePeriapsisS = Math.sqrt((a * a * a) / gm) * (e * Math.sinh(f) - f);
  return {
    dvKmS: accelKmS2 * t,
    capture: {
      periapsisKm: p / (1 + e), leadS: sincePeriapsisS, parkingRadiusKm, sphereKm: null, gmKm3S2: gm,
    },
  };
}

/** Steps between the states a climb track keeps; a step between them is re-flown from the one before. */
const CLIMB_CHECKPOINT_STEPS = 1024;
/** Climbs one memo keeps, the most recently used first. A drive held to a share
 *  of its thrust prices a climb per share it is held to. */
const CLIMB_MEMO_TRACKS = 4;

interface ClimbTrack {
  readonly gm: number;
  readonly parkingRadiusKm: number;
  readonly accelKmS2: number;
  /** The energy at every step flown so far. */
  energy: Float64Array;
  /** x, y, vx, vy at every CLIMB_CHECKPOINT_STEPS-th step. */
  readonly checkpoints: number[];
  steps: number;
  /** The state at step `steps`. */
  readonly at: ClimbState;
  /** No step past `steps` exists: the state went degenerate or the cap was reached. */
  ended: boolean;
}

/** The climbs `finiteCaptureOf` has flown for one caller. The climb from the
 *  circle does not depend on the excess speed, only where it stops, so pricing
 *  the same capture again at another approach reads the steps already flown:
 *  a Jupiter capture is 120,000 of them. The answer is the one a fresh climb gives. */
export interface FiniteCaptureMemo { readonly tracks: ClimbTrack[] }

export function createFiniteCaptureMemo(): FiniteCaptureMemo {
  return { tracks: [] };
}

function climbTrackFor(memo: FiniteCaptureMemo, gm: number, parkingRadiusKm: number, accelKmS2: number): ClimbTrack {
  const tracks = memo.tracks;
  for (let i = 0; i < tracks.length; i++) {
    const t = tracks[i]!;
    if (t.gm === gm && t.parkingRadiusKm === parkingRadiusKm && t.accelKmS2 === accelKmS2) {
      if (i > 0) {
        tracks.splice(i, 1);
        tracks.unshift(t);
      }
      return t;
    }
  }
  const track: ClimbTrack = {
    gm, parkingRadiusKm, accelKmS2,
    energy: new Float64Array(CLIMB_CHECKPOINT_STEPS * 4),
    checkpoints: [],
    steps: 0,
    at: { x: parkingRadiusKm, y: 0, vx: 0, vy: Math.sqrt(gm / parkingRadiusKm) },
    ended: false,
  };
  tracks.unshift(track);
  if (tracks.length > CLIMB_MEMO_TRACKS) tracks.pop();
  return track;
}

/** Fly the memo's climb for this capture on by at most `steps` steps toward
 *  `vInfKmS`, so the pricing that needs it later reads it rather than flying it
 *  inside one frame. True once the climb reaches that speed or ends. */
export function warmFiniteCapture(
  memo: FiniteCaptureMemo, gm: number, parkingRadiusKm: number, accelKmS2: number, vInfKmS: number,
  steps: number,
): boolean {
  if (!(gm > 0) || !(accelKmS2 > 0) || !(parkingRadiusKm > 0) || !(vInfKmS > 0)) return true;
  const track = climbTrackFor(memo, gm, parkingRadiusKm, accelKmS2);
  const targetEnergy = (vInfKmS * vInfKmS) / 2;
  if (track.ended || (track.steps > 0 && track.energy[track.steps - 1]! >= targetEnergy)) return true;
  return climbTo(track, targetEnergy, steps) !== -2;
}

/** The first step whose energy reaches `targetEnergy`, flying the track on as
 *  far as that needs; -1 where the climb ends first, -2 where `stepLimit` new
 *  steps run out first. */
function climbTo(track: ClimbTrack, targetEnergy: number, stepLimit = Infinity): number {
  for (let k = 0; k < track.steps; k++) {
    if (track.energy[k]! >= targetEnergy) return k;
  }
  if (track.ended) return -1;
  const { gm, accelKmS2 } = track;
  const s = track.at;
  const stop = track.steps + stepLimit;
  for (let k = track.steps; k * FINITE_BURN_STEP_S < FINITE_BURN_CAP_S; k++) {
    if (k >= stop) return -2;
    const r = Math.hypot(s.x, s.y);
    const v = Math.hypot(s.vx, s.vy);
    if (!(r > 0) || !(v > 0)) {
      track.ended = true;
      return -1;
    }
    if (k % CLIMB_CHECKPOINT_STEPS === 0) track.checkpoints.push(s.x, s.y, s.vx, s.vy);
    const energy = (v * v) / 2 - gm / r;
    if (k >= track.energy.length) {
      const grown = new Float64Array(track.energy.length * 2);
      grown.set(track.energy);
      track.energy = grown;
    }
    track.energy[k] = energy;
    climbStep(s, gm, accelKmS2, r, v);
    track.steps = k + 1;
    if (energy >= targetEnergy) return k;
  }
  track.ended = true;
  return -1;
}

function captureAtStep(track: ClimbTrack, k: number): { readonly dvKmS: number; readonly capture: FiniteCapture } {
  const { gm, accelKmS2 } = track;
  const from = Math.floor(k / CLIMB_CHECKPOINT_STEPS);
  const cp = track.checkpoints;
  const s: ClimbState = { x: cp[from * 4]!, y: cp[from * 4 + 1]!, vx: cp[from * 4 + 2]!, vy: cp[from * 4 + 3]! };
  for (let j = from * CLIMB_CHECKPOINT_STEPS; j < k; j++) {
    climbStep(s, gm, accelKmS2, Math.hypot(s.x, s.y), Math.hypot(s.vx, s.vy));
  }
  return captureFrom(s, Math.hypot(s.x, s.y), track.energy[k]!, k * FINITE_BURN_STEP_S,
    gm, track.parkingRadiusKm, accelKmS2);
}

/** The capture burn priced as `finiteCaptureOf` flies it, where the context
 *  named a thrust and the capture is into an orbit. Every other case comes back
 *  untouched. */
function pricedCapture(burn: TransferBurn, ctx: TransferContext): TransferBurn {
  const accelMps2 = ctx.shipAccelMps2;
  if (accelMps2 === undefined || !(accelMps2 > 0) || burn.frame !== 'body-relative') return burn;
  const host = bodyById(ctx.rows, burn.referenceId);
  if (host === undefined) return burn;
  const parkingRadiusKm = host.radius_m / 1000 + captureAltitudeKmFor(host, ctx);
  const finite = finiteCaptureOf(gmKm3S2(host), parkingRadiusKm, accelMps2 / 1000, burn.vInfKmS);
  return finite === null ? burn : {
    ...burn,
    dvKmS: finite.dvKmS,
    finiteCapture: { ...finite.capture, sphereKm: sphereOfInfluenceKm(host, ctx.rows) },
  };
}

interface DepartureImpulse {
  readonly dvKmS: number;
  readonly sense: BurnSense;
  readonly point: LocalBurnPoint;
}

/** Angle from the ship's radius round to the conic's outgoing asymptote,
 *  measured the way the ship travels, for a post-burn velocity at flight path
 *  angle `gammaRad`. Falls from 2pi at -pi/2 to 0 at +pi/2, so one root. */
function asymptoteSweepRad(
  gammaRad: number,
  gm: number,
  radiusKm: number,
  speedKmS: number,
  vInfKmS: number,
): number {
  const momentum = radiusKm * speedKmS * Math.cos(gammaRad);
  const eccentricity = Math.sqrt(1 + (vInfKmS * vInfKmS * momentum * momentum) / (gm * gm));
  const toAsymptote = Math.acos(clampUnit(-1 / eccentricity));
  const trueAnomaly = Math.acos(
    clampUnit(((momentum * momentum) / (gm * radiusKm) - 1) / eccentricity),
  );
  return toAsymptote - (gammaRad < 0 ? -trueAnomaly : trueAnomaly);
}

function clampUnit(x: number): number {
  return x < -1 ? -1 : x > 1 ? 1 : x;
}

/** Bisection steps a departure is solved to, and the coarser count the orbit
 *  sweep RANKS candidate points with: 20 steps put the flight path angle inside
 *  3e-6 rad, which ranks prices to about 1e-5 km/s, and the point that wins is
 *  then priced at the full count. */
const BISECTION_STEPS = 60;
const SCAN_BISECTION_STEPS = 20;

/** Flight path angle whose conic sweeps `targetRad` from the ship's radius to
 *  its asymptote. Bisection, since `asymptoteSweepRad` is monotone. */
function solveFlightPathRad(
  targetRad: number,
  gm: number,
  radiusKm: number,
  speedKmS: number,
  vInfKmS: number,
  steps: number,
): number {
  let lo = -Math.PI / 2;
  let hi = Math.PI / 2;
  for (let i = 0; i < steps; i++) {
    const mid = (lo + hi) / 2;
    if (asymptoteSweepRad(mid, gm, radiusKm, speedKmS, vInfKmS) > targetRad) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** The impulse onto the conic that leaves the ship's own point along
 *  `asymptote`: energy fixes its speed, the solve above its direction, and of
 *  the two ways round the body the cheaper one clearing the surface is the
 *  price - a reason where neither does. A circular orbit's departure point is
 *  free instead, and the plan names the one whose conic comes out along it. */
function departureImpulse(
  well: DepartureWell,
  referenceId: string,
  vInfKmS: number,
  asymptote: Vec3,
  bisectionSteps: number = BISECTION_STEPS,
): DepartureImpulse | string {
  const requiredSpeedKmS = Math.sqrt(vInfKmS * vInfKmS + (2 * well.gm) / well.radiusKm);
  if (well.circular) {
    const axis = unit(cross(well.positionKm, well.velocityKmS));
    const flat = sub(asymptote, scale(axis, dot(asymptote, axis)));
    const outgoing = norm(flat) > 0 ? unit(flat) : unit(well.positionKm);
    const eccentricity = 1 + (well.radiusKm * vInfKmS * vInfKmS) / well.gm;
    const radial = rotateAbout(outgoing, axis, -Math.acos(-1 / eccentricity));
    const prograde = unit(cross(axis, radial));
    const signed = requiredSpeedKmS - well.speedKmS;
    return {
      dvKmS: Math.abs(signed),
      sense: signed >= 0 ? 'prograde' : 'retrograde',
      point: {
        referenceId,
        positionKm: scale(radial, well.radiusKm),
        velocityKmS: scale(prograde, well.speedKmS),
        thrustDirection: signed >= 0 ? prograde : scale(prograde, -1),
      },
    };
  }
  const outward = unit(well.positionKm);
  const aim = unit(asymptote);
  // The conic lies in the plane of the ship's radius and the asymptote. Where
  // those are parallel that plane is the ship's own orbit plane instead, and
  // where the velocity is radial too there is no plane to solve in.
  let plane = cross(outward, aim);
  if (norm(plane) < 1e-9) {
    plane = cross(outward, well.velocityKmS);
    if (well.speedKmS <= 0 || norm(plane) < 1e-9 * well.speedKmS) {
      return `the ship, ${referenceId} and the transfer's asymptote sit on one line`;
    }
  }
  const pole = unit(plane);
  const sweepRad = Math.acos(clampUnit(dot(outward, aim)));
  let best: { readonly dvKmS: number; readonly impulse: Vec3 } | null = null;
  for (const way of [1, -1]) {
    const gammaRad = solveFlightPathRad(
      way > 0 ? sweepRad : TWO_PI - sweepRad,
      well.gm,
      well.radiusKm,
      requiredSpeedKmS,
      vInfKmS,
      bisectionSteps,
    );
    const momentum = well.radiusKm * requiredSpeedKmS * Math.cos(gammaRad);
    const eccentricity = Math.sqrt(
      1 + (vInfKmS * vInfKmS * momentum * momentum) / (well.gm * well.gm),
    );
    const periapsisKm = (momentum * momentum) / (well.gm * (1 + eccentricity));
    // A ship already outbound never reaches the new conic's periapsis, so only
    // one it still has to fly through has to clear the ground.
    if (gammaRad < 0 && periapsisKm < well.surfaceRadiusKm) continue;
    const transverse = scale(cross(pole, outward), way);
    const after = add(
      scale(outward, requiredSpeedKmS * Math.sin(gammaRad)),
      scale(transverse, requiredSpeedKmS * Math.cos(gammaRad)),
    );
    const impulse = sub(after, well.velocityKmS);
    const dvKmS = norm(impulse);
    if (best === null || dvKmS < best.dvKmS) best = { dvKmS, impulse };
  }
  if (best === null) {
    return `every escape conic onto this transfer's asymptote passes through ${referenceId}`;
  }
  return {
    dvKmS: best.dvKmS,
    sense: senseOf(best.impulse, well.velocityKmS),
    point: {
      referenceId,
      positionKm: well.positionKm,
      velocityKmS: well.velocityKmS,
      thrustDirection: best.dvKmS > 0 ? scale(best.impulse, 1 / best.dvKmS) : ZERO_VEC,
    },
  };
}

/** True anomalies one revolution is scanned at, and the refinement that follows
 *  the best of them down: three rounds, each a fifth of the last, which lands
 *  the departure point inside 0.1 degrees. Nearer the minimum than that is
 *  worth under 1e-5 km/s and the sweep is the whole cost of a survey. */
const ORBIT_SCAN_STEPS = 36;
const ORBIT_REFINE_ROUNDS = 3;
const ORBIT_REFINE_STEPS = 8;
const ORBIT_REFINE_SHRINK = 5;

/** The ship's state at one true anomaly of its own closed orbit, as a well the
 *  departure solve can be asked about. */
function wellOnOrbit(well: DepartureWell, orbit: ClosedOrbit, trueAnomalyRad: number): DepartureWell {
  const c = Math.cos(trueAnomalyRad);
  const s = Math.sin(trueAnomalyRad);
  const radiusKm = orbit.semiLatusKm / (1 + orbit.eccentricity * c);
  const k = Math.sqrt(well.gm / orbit.semiLatusKm);
  const positionKm = add(
    scale(orbit.periapsisDir, radiusKm * c),
    scale(orbit.transverseDir, radiusKm * s),
  );
  const velocityKmS = add(
    scale(orbit.periapsisDir, -k * s),
    scale(orbit.transverseDir, k * (orbit.eccentricity + c)),
  );
  return {
    gm: well.gm,
    radiusKm,
    speedKmS: norm(velocityKmS),
    positionKm,
    velocityKmS,
    surfaceRadiusKm: well.surfaceRadiusKm,
    circular: false,
    orbit: null,
  };
}

/** Semi-major axis, km, from a closed orbit's own semi-latus rectum and
 *  eccentricity: p = a(1 - e^2). */
function semiMajorKm(orbit: ClosedOrbit): number {
  return orbit.semiLatusKm / (1 - orbit.eccentricity * orbit.eccentricity);
}

/** True anomaly, radians, of a position already known to lie on the orbit. */
function trueAnomalyOnOrbit(positionKm: Vec3, orbit: ClosedOrbit): number {
  return Math.atan2(dot(positionKm, orbit.transverseDir), dot(positionKm, orbit.periapsisDir));
}

/** Mean anomaly from true anomaly and eccentricity: the inverse of the trig
 *  `kepler()` in ephemeris.ts solves. */
function meanAnomalyOf(trueAnomalyRad: number, e: number): number {
  const eccentricRad = Math.atan2(
    Math.sqrt(1 - e * e) * Math.sin(trueAnomalyRad),
    e + Math.cos(trueAnomalyRad),
  );
  return eccentricRad - e * Math.sin(eccentricRad);
}

/** True anomaly from mean anomaly and eccentricity, off the same eccentric-
 *  anomaly solve `src/world/ephemeris.ts` runs for every registry body's own
 *  position - not a second copy of it. */
function trueAnomalyAfter(meanAnomalyRad: number, e: number): number {
  const eccentricRad = kepler(meanAnomalyRad, e);
  return Math.atan2(Math.sqrt(1 - e * e) * Math.sin(eccentricRad), Math.cos(eccentricRad) - e);
}

/**
 * The ship's own state on its closed orbit `dtSeconds` after the epoch `well`
 * was read at: a genuine Kepler propagation, exact for an eccentric orbit and,
 * at the tiny eccentricity a near-circular one reads as, indistinguishable
 * from the old fixed circular rate - so one path serves both rather than a
 * second copy of the physics for each.
 */
function wellAfter(well: DepartureWell, orbit: ClosedOrbit, dtSeconds: number): DepartureWell {
  const meanMotionRadPerS = Math.sqrt(well.gm / Math.pow(semiMajorKm(orbit), 3));
  const nu0 = trueAnomalyOnOrbit(well.positionKm, orbit);
  const meanAnomalyRad = meanAnomalyOf(nu0, orbit.eccentricity) + meanMotionRadPerS * dtSeconds;
  const nuAfter = trueAnomalyAfter(meanAnomalyRad, orbit.eccentricity);
  return wellOnOrbit(well, orbit, nuAfter);
}

/** `wellAfter` read back as a plain heliocentric state, for a caller carrying
 *  a conic forward that has no body's well to report a burn against. */
function stateFromWell(
  well: DepartureWell,
  orbit: ClosedOrbit,
  epochJd0: number,
  epochJd: number,
): State {
  const after = wellAfter(well, orbit, (epochJd - epochJd0) * SECONDS_PER_DAY);
  return { position: after.positionKm, velocity: after.velocityKmS };
}

/**
 * The cheapest departure anywhere on one revolution of the ship's own closed
 * orbit: a coarse scan of true anomaly, then refinements around the best of it.
 *
 * The orbit is fixed in the body's own inertial frame and the window is many
 * revolutions away, so the ship reaches every point scanned here with time to
 * spare. The scan is coarse because the price is smooth in true anomaly except
 * where the clearing candidate changes over, and the refinement is what puts
 * the answer on the minimum rather than on a grid line. It is anchored on the
 * ship's OWN true anomaly, so the point the ship is at is always one of the
 * candidates and this can never come back dearer than pricing the burn there.
 */
function cheapestOnOrbit(
  well: DepartureWell,
  orbit: ClosedOrbit,
  referenceId: string,
  vInfKmS: number,
  asymptote: Vec3,
): DepartureImpulse | string {
  let reason = `every escape conic onto this transfer's asymptote passes through ${referenceId}`;
  const priceAt = (trueAnomalyRad: number, steps: number): DepartureImpulse | null => {
    const at = departureImpulse(
      wellOnOrbit(well, orbit, trueAnomalyRad), referenceId, vInfKmS, asymptote, steps,
    );
    if (typeof at === 'string') {
      reason = at;
      return null;
    }
    return at;
  };
  const shipRad = Math.atan2(
    dot(well.positionKm, orbit.transverseDir),
    dot(well.positionKm, orbit.periapsisDir),
  );
  let best: DepartureImpulse | null = null;
  let bestRad = shipRad;
  for (let i = 0; i < ORBIT_SCAN_STEPS; i++) {
    const trueAnomalyRad = shipRad + (TWO_PI * i) / ORBIT_SCAN_STEPS;
    const at = priceAt(trueAnomalyRad, SCAN_BISECTION_STEPS);
    if (at !== null && (best === null || at.dvKmS < best.dvKmS)) {
      best = at;
      bestRad = trueAnomalyRad;
    }
  }
  if (best === null) return reason;
  let spanRad = TWO_PI / ORBIT_SCAN_STEPS;
  for (let round = 0; round < ORBIT_REFINE_ROUNDS; round++) {
    const centreRad = bestRad;
    for (let i = 0; i <= ORBIT_REFINE_STEPS; i++) {
      const trueAnomalyRad = centreRad - spanRad + (2 * spanRad * i) / ORBIT_REFINE_STEPS;
      const at = priceAt(trueAnomalyRad, SCAN_BISECTION_STEPS);
      if (at !== null && at.dvKmS < best.dvKmS) {
        best = at;
        bestRad = trueAnomalyRad;
      }
    }
    spanRad /= ORBIT_REFINE_SHRINK;
  }
  return priceAt(bestRad, BISECTION_STEPS) ?? best;
}

/** The revolution a departure is priced over, or null where the ship is priced
 *  at its own point: a circular orbit, whose point is free and named; a conic
 *  that never comes round; and the caller asking for the point price. */
function sweptOrbit(well: DepartureWell, ctx: TransferContext): ClosedOrbit | null {
  if (ctx.departureAtShipPoint === true || well.circular) return null;
  return well.orbit;
}

const DEG_TO_RAD = Math.PI / 180;

/** Unit normal of a closed orbit from its own inclination and node, ecliptic
 *  J2000 axes: the standard rotation of the orbital-frame pole by inclination
 *  then node, with the argument of periapsis left out since it turns the pole
 *  about itself. */
function orbitNormal(iRad: number, nodeRad: number): Vec3 {
  const si = Math.sin(iRad);
  return { x: si * Math.sin(nodeRad), y: -si * Math.cos(nodeRad), z: Math.cos(iRad) };
}

/**
 * Degrees the moon's own orbital plane sits out of the ship's, 0 to 90: a
 * moon whose plane matches the ship's, however it is travelled, reads near 0
 * either sign of the pole, so it is the angle to the NEARER of the pole or
 * its opposite that says how far out of plane the moon really is.
 *
 * The plane is the one the moon's elements have AT THIS EPOCH - the same
 * propagation, osculating sidecar and all, that its position comes from. Off
 * the row's own J2000 fit instead it reads Phobos 1.1 degrees from where the
 * ephemeris actually draws it in 2078, on a figure the map now prints.
 */
function planeMismatchDeg(
  pole: Vec3,
  orbit: Orbit,
  epochJd: number,
  fromJ2000: boolean,
): number {
  const at = fromJ2000
    ? { iRad: orbit.i_deg * DEG_TO_RAD, nodeRad: orbit.long_node_deg * DEG_TO_RAD }
    : elementsAt(orbit, epochJd);
  const cosAngle = clampUnit(dot(pole, orbitNormal(at.iRad, at.nodeRad)));
  const angleDeg = (Math.acos(cosAngle) * 180) / Math.PI;
  return Math.min(angleDeg, 180 - angleDeg);
}

/** Past this mismatch the moon swings further out of the ship's own plane
 *  than round it (sin > cos of the angle): the boot orbit's own 13.4 degree
 *  Phobos mismatch sits well under it, Triton's and Charon's well over. */
const MAX_PLANE_MISMATCH_DEG = 45;

/**
 * Can a ship orbit this moon at `captureRadiusKm`?
 *
 * The moon holds what its own sphere of influence about its parent holds, and
 * nothing else: Phobos's is 7.2 km where the moon itself is 11.1 km across, so
 * the whole of it is inside the rock and an arrival there is a velocity match.
 * Read off the registry's own masses and distances, not off a list of names.
 */
function holdsAnOrbit(
  moon: BodyRow,
  captureRadiusKm: number,
  rows: readonly BodyRow[],
): boolean {
  const holdKm = sphereOfInfluenceKm(moon, rows);
  return holdKm !== null && holdKm > captureRadiusKm && gmKm3S2(moon) > 0;
}

/**
 * The window a moon trip is searched over, and the flight times each chance
 * in it is priced at.
 *
 * A turn into the moon's plane only lands in that plane when it is made on the
 * line where the two planes cross, so a trip turns either where the ship's own
 * orbit crosses the moon's plane, as it leaves, or where the moon crosses the
 * ship's, as it arrives. The moon is almost never at the far end of a half-orbit
 * ellipse from such a point - from a 20,428 km orbit the first departure that
 * puts Phobos within 2 degrees of it is 20 days out - so each crossing is priced
 * as the conic that actually reaches the moon from it (`lambert`), at flight
 * times from half to one and a half of the flat ellipse's own, and the plan is
 * the cheapest crossing in one revolution of whichever of the ship and the moon
 * is slower.
 */
const MOON_TOF_LO_FRACTION = 0.5;
const MOON_TOF_HI_FRACTION = 1.5;
/** Flight times tried per revolution of the body a step moves, before the
 *  cheapest is refined, and the fewest and most one crossing is tried at. */
const MOON_TOF_STEPS_PER_TURN = 24;
const MOON_TOF_MIN_STEPS = 8;
const MOON_TOF_MAX_STEPS = 96;
/** Halvings the refinement round the cheapest flight time runs: from a
 *  twenty-fourth of an orbit to a small fraction of a second. */
const MOON_TOF_REFINE_ROUNDS = 24;
/** Samples per revolution of the moon its crossings of the ship's plane are
 *  bracketed at, and the bisections each one is then dated to. */
const MOON_CROSSING_STEPS = 24;
const MOON_CROSSING_BISECTIONS = 48;
/** How much dearer than the other end the closed-form turn at one end may be
 *  before `ctx.cheaperEndOnly` leaves that end's crossings unsearched. Both are
 *  searched otherwise: from an eccentric orbit the closed form, read off the
 *  ship's point now, misses the cheaper end by as much as half the price. */
const MOON_END_MARGIN = 0.25;
/** Crossings priced at each end, earliest first. A low orbit crosses the moon's
 *  plane every 46 minutes, so a month-long revolution of the Moon is 870 of
 *  them, where the first dozen already hold every phase the flight time can
 *  reach. */
const MOON_MAX_CROSSINGS = 12;
/** How much wider than its own claimed span `ctx.lieAboutWindowSpan` searches:
 *  the control on the gate barring `windowWaitDays` to `synodicPeriodDays`. */
const WINDOW_SPAN_LIE_FACTOR = 20;

/** One way to the moon: where and when the ship leaves, what it pushes, and
 *  what the arrival then has to take out. */
interface MoonLeg {
  readonly departureEpochJd: number;
  readonly arrivalEpochJd: number;
  /** The ship at the departure, about the parent, km and km/s. */
  readonly positionKm: Vec3;
  readonly velocityKmS: Vec3;
  readonly impulse: Vec3;
  /** Speed against the moon on arrival, and the direction that takes it out. */
  readonly relativeKmS: number;
  readonly captureDirection: Vec3;
  readonly semiMajorKm: number;
  readonly arrivalRadiusKm: number;
  readonly turnAtDeparture: boolean;
  readonly totalKmS: number;
  /** Days the search the leg came from covers, which is the wait it bounds. */
  readonly windowPeriodDays: number;
}

/**
 * A moon of the body the ship is orbiting: two impulses, in that body's frame
 * and on its own mass.
 *
 * The ship's own orbit plane about the parent and the moon's plane are
 * `planeTiltDeg` apart. The turn between them is made on their line of nodes:
 * either the departure is dated to the ship crossing the moon's plane and the
 * whole crossing is flown in the moon's plane, or the arrival is dated to the
 * moon crossing the ship's and the crossing is flown in the ship's own. Each
 * such crossing is priced as the conic that reaches the moon's real position
 * from the ship's real one (see MOON_TOF_LO_FRACTION), and the plan is the
 * cheapest. The ship is carried to its departure point by a Kepler propagation
 * of its own closed orbit, so an eccentric orbit leaves from where it really
 * is. An escaping ship, or one whose orbit never clears the ground, has no such
 * point to be carried to and is refused rather than priced off a snapshot that
 * stops being true within the wait.
 *
 * `ctx.turnAboutBurnRadius` puts back the plan this replaced: the window on the
 * projected phase alone and the departure turned about the burn radius.
 */
function* planMoonTransfer(
  departure: Departure,
  well: DepartureWell,
  parent: BodyRow,
  moon: BodyRow,
  ctx: TransferContext,
): Generator<void, TransferPlan | string, void> {
  const orbit = moon.orbit;
  if (orbit === null) return 'the centre of every orbit';
  const gm = well.gm;
  const r1 = well.radiusKm;
  const r2 = orbit.a_au * AU_KM;
  if (gm <= 0) return `${parent.display_name} carries no mass to transfer around`;
  if (!(Number.isFinite(r1) && r1 > 0) || !(Number.isFinite(r2) && r2 > 0)) {
    return 'no distance to transfer between';
  }
  if (Math.abs(r2 - r1) / r1 < 1e-6) {
    return `the same distance from ${parent.display_name}, so no transfer and no window`;
  }
  const momentum = cross(well.positionKm, well.velocityKmS);
  if (norm(momentum) <= 0) return `the ship is falling straight at ${parent.display_name}`;
  const pole = unit(momentum);

  const shipOrbit = well.orbit;
  if (shipOrbit === null) {
    const energyKm2S2 = (well.speedKmS * well.speedKmS) / 2 - gm / r1;
    if (energyKm2S2 >= 0) {
      return `on an escape path from ${parent.display_name}: get into orbit round ${parent.display_name} first`;
    }
    return `an orbit round ${parent.display_name} that never clears the ground: get into a clean orbit first`;
  }

  const mismatchDeg = planeMismatchDeg(pole, orbit, ctx.epochJd, ctx.planeFromJ2000 === true);
  if (mismatchDeg > MAX_PLANE_MISMATCH_DEG) {
    return `${moon.display_name} orbits ${Math.round(mismatchDeg)} degrees out of the plane the ship is on: no coplanar transfer reaches it from here`;
  }

  const outwardDir = unit(well.positionKm);
  const acrossDir = cross(pole, outwardDir);

  // The ship's own true position, velocity and radius `dtSeconds` from the
  // epoch the plan was asked for - a Kepler propagation of its own orbit, not
  // a fixed rate spun round a snapshot that stops being true within the wait.
  const shipStateAt = (epochJd: number): { positionKm: Vec3; velocityKmS: Vec3; radiusKm: number } => {
    const after = wellAfter(well, shipOrbit, (epochJd - ctx.epochJd) * SECONDS_PER_DAY);
    return { positionKm: after.positionKm, velocityKmS: after.velocityKmS, radiusKm: after.radiusKm };
  };
  // The transfer ellipse itself depends on r1, which moves with the ship's
  // own orbit over the wait - so it is a function of the epoch tried rather
  // than a single figure fixed at the epoch the plan was asked for.
  const transferAt = (departR1: number) => {
    const aTransferKm = (departR1 + r2) / 2;
    const vCircle1 = Math.sqrt(gm / departR1);
    const vCircle2 = Math.sqrt(gm / r2);
    const vDepart = Math.sqrt(gm * (2 / departR1 - 1 / aTransferKm));
    const vArrive = Math.sqrt(gm * (2 / r2 - 1 / aTransferKm));
    const tofSeconds = Math.PI * Math.sqrt((aTransferKm * aTransferKm * aTransferKm) / gm);
    return {
      aTransferKm,
      vDepart,
      vArrive,
      vCircle2,
      vInfDepart: Math.abs(vDepart - vCircle1),
      vInfArrive: Math.abs(vCircle2 - vArrive),
      timeOfFlightDays: tofSeconds / SECONDS_PER_DAY,
    };
  };

  const n1 = Math.sqrt(gm / Math.pow(semiMajorKm(shipOrbit), 3));
  const n2 = Math.sqrt(gm / (r2 * r2 * r2));
  const rate = n2 - n1;
  if (rate === 0) return 'the same mean motion, so no window ever opens';
  const synodicPeriodDays = TWO_PI / Math.abs(rate) / SECONDS_PER_DAY;

  const captureRadiusKm = moon.radius_m / 1000 + captureAltitudeKmFor(moon, ctx);
  const captureGm = gmKm3S2(moon);
  const holds = holdsAnOrbit(moon, captureRadiusKm, ctx.rows);
  const captureSignedFor = (relativeKmS: number): number => (holds
    ? wellBurnKmS(captureGm, captureRadiusKm, Math.sqrt(captureGm / captureRadiusKm), relativeKmS)
    : relativeKmS);
  const tiltRad = (mismatchDeg * Math.PI) / 180;
  // A turn costs the speed it is made at, so the cheaper end is the slow end
  // of the ellipse - the departure going inward, the arrival going outward.
  const turnedRelativeFor = (transfer: ReturnType<typeof transferAt>): number => Math.sqrt(Math.max(
    0,
    transfer.vArrive * transfer.vArrive + transfer.vCircle2 * transfer.vCircle2
      - 2 * transfer.vArrive * transfer.vCircle2 * Math.cos(tiltRad),
  ));

  // The plan this replaced, kept as the control: the window read off the moon's
  // angle projected into the ship's plane, and the departure turned about the
  // burn radius, which is only the right axis on the line of nodes.
  const legOnPhase = (): MoonLeg => {
    const moonAngleAt = (epochJd: number): number => {
      const centre = heliocentric(parent, ctx.rows, epochJd).position;
      const at = sub(heliocentric(moon, ctx.rows, epochJd).position, centre);
      return Math.atan2(dot(at, acrossDir), dot(at, outwardDir));
    };
    const phaseAt = (epochJd: number): number => {
      const ship = shipStateAt(epochJd);
      const transfer = transferAt(ship.radiusKm);
      const shipAngle = Math.atan2(dot(ship.positionKm, acrossDir), dot(ship.positionKm, outwardDir));
      return wrapTwoPi(moonAngleAt(epochJd + transfer.timeOfFlightDays) - (Math.PI + shipAngle));
    };
    const departureEpochJd = ctx.epochJd + solveWindowDays(phaseAt, ctx, 0, rate);
    const ship = shipStateAt(departureEpochJd);
    const transfer = transferAt(ship.radiusKm);
    const alongDeparture = cross(pole, unit(ship.positionKm));
    const flatImpulse = sub(scale(alongDeparture, transfer.vDepart), ship.velocityKmS);
    const turnedAlong = rotateAbout(alongDeparture, unit(ship.positionKm), tiltRad);
    const turnedImpulse = sub(scale(turnedAlong, transfer.vDepart), ship.velocityKmS);
    const turnedRelativeKmS = turnedRelativeFor(transfer);
    const flatCaptureKmS = Math.abs(captureSignedFor(transfer.vInfArrive));
    const turnAtDeparture = norm(turnedImpulse) + flatCaptureKmS
      <= norm(flatImpulse) + Math.abs(captureSignedFor(turnedRelativeKmS));
    const impulse = turnAtDeparture ? turnedImpulse : flatImpulse;
    const relativeKmS = turnAtDeparture ? transfer.vInfArrive : turnedRelativeKmS;
    const arrivalDir = rotateAbout(unit(ship.positionKm), pole, Math.PI);
    const alongArrival = cross(pole, arrivalDir);
    return {
      departureEpochJd,
      arrivalEpochJd: departureEpochJd + transfer.timeOfFlightDays,
      positionKm: ship.positionKm,
      velocityKmS: ship.velocityKmS,
      impulse,
      relativeKmS,
      captureDirection: r2 > ship.radiusKm ? alongArrival : scale(alongArrival, -1),
      semiMajorKm: transfer.aTransferKm,
      arrivalRadiusKm: r2,
      turnAtDeparture,
      totalKmS: norm(impulse) + Math.abs(captureSignedFor(relativeKmS)),
      windowPeriodDays: synodicPeriodDays,
    };
  };

  function* legOnNodes(): Generator<void, MoonLeg | string, void> {
    if (orbit === null || shipOrbit === null) return 'the centre of every orbit';
    const moonStateAt = (epochJd: number): State => {
      const centre = heliocentric(parent, ctx.rows, epochJd);
      const at = heliocentric(moon, ctx.rows, epochJd);
      return { position: sub(at.position, centre.position), velocity: sub(at.velocity, centre.velocity) };
    };
    const moonPoleAt = (epochJd: number): Vec3 => {
      const at = elementsAt(orbit, epochJd);
      return orbitNormal(at.iRad, at.nodeRad);
    };
    const flat = transferAt(r1);
    const spanDays = TWO_PI / Math.min(n1, n2) / SECONDS_PER_DAY;
    const tofLoDays = flat.timeOfFlightDays * MOON_TOF_LO_FRACTION;
    const tofHiDays = flat.timeOfFlightDays * MOON_TOF_HI_FRACTION;
    // Stepping the flight time from a fixed departure moves the arrival round
    // the moon's orbit; from a fixed arrival it moves the departure round the
    // ship's, so each end is gridded at the rate of the body it moves.
    const tofStepDaysAt = (meanMotion: number): number => {
      const turns = ((tofHiDays - tofLoDays) * SECONDS_PER_DAY * meanMotion) / TWO_PI;
      const steps = Math.min(MOON_TOF_MAX_STEPS, Math.max(MOON_TOF_MIN_STEPS,
        Math.ceil(turns * MOON_TOF_STEPS_PER_TURN)));
      return (tofHiDays - tofLoDays) / steps;
    };
    // `ctx.lieAboutWindowSpan` searches past the span the leg reports below,
    // so a window genuinely found out there is the control that proves the
    // gate barring `windowWaitDays` to that span can fail.
    const searchSpanDays = ctx.lieAboutWindowSpan === true
      ? spanDays * WINDOW_SPAN_LIE_FACTOR
      : spanDays;
    const lastDepartureJd = ctx.epochJd + searchSpanDays;

    // Under the control, only the end whose closed-form turn is cheaper.
    let searchDeparture = true;
    let searchArrival = true;
    if (ctx.cheaperEndOnly === true) {
      const flatCaptureKmS = Math.abs(captureSignedFor(flat.vInfArrive));
      const alongNow = cross(pole, unit(well.positionKm));
      const flatDepartKmS = Math.abs(flat.vDepart - norm(well.velocityKmS));
      const turnedDepartKmS = norm(sub(
        scale(rotateAbout(alongNow, unit(well.positionKm), tiltRad), flat.vDepart),
        scale(alongNow, norm(well.velocityKmS)),
      ));
      const atDepartureKmS = turnedDepartKmS + flatCaptureKmS;
      const atArrivalKmS = flatDepartKmS + Math.abs(captureSignedFor(turnedRelativeFor(flat)));
      const cheaperEndKmS = Math.min(atDepartureKmS, atArrivalKmS);
      searchDeparture = atDepartureKmS <= cheaperEndKmS * (1 + MOON_END_MARGIN);
      searchArrival = atArrivalKmS <= cheaperEndKmS * (1 + MOON_END_MARGIN);
    }

    let sinceYield = 0;
    const reversePole = scale(pole, -1);
    // A leg that turns as it arrives flies only the ship's own way round; one that
    // turns as it leaves is steered onto either conic, so both ways are priced.
    const legBetween = (
      departJd: number,
      arriveJd: number,
      turnAtDeparture: boolean,
    ): MoonLeg | null => {
      const tofSeconds = (arriveJd - departJd) * SECONDS_PER_DAY;
      if (!(tofSeconds > 0)) return null;
      const ship = shipStateAt(departJd);
      const target = moonStateAt(arriveJd);
      const legAbout = (hint: Vec3): MoonLeg | null => {
        const solution = lambert(ship.positionKm, target.position, tofSeconds, gm, hint);
        if (!solution.ok) return null;
        const speed = norm(solution.v1);
        const semiMajor = 1 / (2 / ship.radiusKm - (speed * speed) / gm);
        if (!(semiMajor > 0)) return null;
        const momentumKm2S = norm(cross(ship.positionKm, solution.v1));
        const e = Math.sqrt(Math.max(0, 1 - (momentumKm2S * momentumKm2S) / (gm * semiMajor)));
        if (semiMajor * (1 - e) < well.surfaceRadiusKm) return null;
        const impulse = sub(solution.v1, ship.velocityKmS);
        const arrival = sub(target.velocity, solution.v2);
        const relativeKmS = norm(arrival);
        return {
          departureEpochJd: departJd,
          arrivalEpochJd: arriveJd,
          positionKm: ship.positionKm,
          velocityKmS: ship.velocityKmS,
          impulse,
          relativeKmS,
          captureDirection: unit(arrival),
          semiMajorKm: semiMajor,
          arrivalRadiusKm: norm(target.position),
          turnAtDeparture,
          totalKmS: norm(impulse) + Math.abs(captureSignedFor(relativeKmS)),
          windowPeriodDays: spanDays,
        };
      };
      const shipWay = legAbout(pole);
      if (!turnAtDeparture || ctx.shipPoleSense === true) return shipWay;
      const otherWay = legAbout(reversePole);
      if (otherWay === null) return shipWay;
      return shipWay === null || otherWay.totalKmS < shipWay.totalKmS ? otherWay : shipWay;
    };
    // The cheapest flight time from one crossing: a grid, then a bracket
    // halved round its best point.
    function* cheapestFrom(
      priceAt: (tofDays: number) => MoonLeg | null,
      tofStepDays: number,
    ): Generator<void, MoonLeg | null, void> {
      let best: MoonLeg | null = null;
      let bestTof = tofLoDays;
      const tryAt = (tofDays: number): void => {
        const leg = priceAt(tofDays);
        if (leg !== null && (best === null || leg.totalKmS < best.totalKmS)) {
          best = leg;
          bestTof = tofDays;
        }
      };
      for (let tofDays = tofLoDays; tofDays <= tofHiDays + tofStepDays / 2; tofDays += tofStepDays) {
        tryAt(tofDays);
        if (++sinceYield >= WINDOW_SOLVE_SLICE) {
          sinceYield = 0;
          yield;
        }
      }
      if (best === null) return null;
      let spanTof = tofStepDays;
      for (let round = 0; round < MOON_TOF_REFINE_ROUNDS; round++) {
        const centre = bestTof;
        tryAt(centre - spanTof);
        tryAt(centre + spanTof);
        spanTof /= 2;
        sinceYield += 2;
        if (sinceYield >= WINDOW_SOLVE_SLICE) {
          sinceYield = 0;
          yield;
        }
      }
      return best;
    }

    let best: MoonLeg | null = null;
    const keep = (leg: MoonLeg | null): void => {
      if (leg !== null && (best === null || leg.totalKmS < best.totalKmS)) best = leg;
    };
    const lineAt = (epochJd: number): Vec3 | null => {
      const line = cross(pole, moonPoleAt(epochJd));
      return norm(line) > 1e-12 ? unit(line) : null;
    };

    if (searchDeparture) {
      // Turn as it leaves: dated to the ship crossing the moon's plane, the
      // plane the moon is in when the ship gets there.
      const eccentricity = shipOrbit.eccentricity;
      const meanNow = meanAnomalyOf(trueAnomalyOnOrbit(well.positionKm, shipOrbit), eccentricity);
      const anomalyOf = (dir: Vec3): number =>
        Math.atan2(dot(dir, shipOrbit.transverseDir), dot(dir, shipOrbit.periapsisDir));
      const periodS = TWO_PI / n1;
      const first = lineAt(ctx.epochJd + flat.timeOfFlightDays) ?? unit(well.positionKm);
      const departures: number[] = [];
      for (const side of [0, Math.PI]) {
        const nu0 = anomalyOf(first) + side;
        const t0 = wrapTwoPi(meanAnomalyOf(nu0, eccentricity) - meanNow) / n1;
        for (let t = t0; t <= searchSpanDays * SECONDS_PER_DAY; t += periodS) {
          const line = lineAt(ctx.epochJd + t / SECONDS_PER_DAY + flat.timeOfFlightDays) ?? first;
          const nu = anomalyOf(line) + side;
          const shift = wrapPi(meanAnomalyOf(nu, eccentricity) - meanAnomalyOf(nu0, eccentricity)) / n1;
          const departJd = ctx.epochJd + (t + shift) / SECONDS_PER_DAY;
          if (departJd >= ctx.epochJd && departJd <= lastDepartureJd) departures.push(departJd);
        }
      }
      departures.sort((a, b) => a - b);
      for (const departJd of departures.slice(0, MOON_MAX_CROSSINGS)) {
        keep(yield* cheapestFrom(
          (tofDays) => legBetween(departJd, departJd + tofDays, true),
          tofStepDaysAt(n2),
        ));
      }
    }
    if (searchArrival && lineAt(ctx.epochJd) !== null) {
      // Turn as it arrives: dated to the moon crossing the ship's own plane.
      const offPlane = (epochJd: number): number => dot(moonStateAt(epochJd).position, pole);
      const stepDays = TWO_PI / n2 / MOON_CROSSING_STEPS / SECONDS_PER_DAY;
      let loJd = ctx.epochJd + tofLoDays;
      let loOff = offPlane(loJd);
      const lastArrivalJd = lastDepartureJd + tofHiDays;
      let crossings = 0;
      while (loJd < lastArrivalJd && crossings < MOON_MAX_CROSSINGS) {
        const hiJd = loJd + stepDays;
        const hiOff = offPlane(hiJd);
        if (loOff === 0 || (loOff < 0) !== (hiOff < 0)) {
          let a = loJd;
          let b = hiJd;
          let aOff = loOff;
          for (let i = 0; i < MOON_CROSSING_BISECTIONS; i++) {
            const mid = (a + b) / 2;
            const midOff = offPlane(mid);
            if ((midOff < 0) === (aOff < 0)) {
              a = mid;
              aOff = midOff;
            } else {
              b = mid;
            }
          }
          const arriveJd = (a + b) / 2;
          crossings++;
          keep(yield* cheapestFrom((tofDays) => {
            const departJd = arriveJd - tofDays;
            return departJd < ctx.epochJd || departJd > lastDepartureJd
              ? null
              : legBetween(departJd, arriveJd, false);
          }, tofStepDaysAt(n1)));
        }
        loJd = hiJd;
        loOff = hiOff;
      }
    }
    return best ?? `no crossing to ${moon.display_name} reaches it within ${(spanDays * 24).toFixed(1)} h`;
  }

  const leg = ctx.turnAboutBurnRadius === true ? legOnPhase() : yield* legOnNodes();
  if (typeof leg === 'string') return leg;

  const departureRadiusKm = norm(leg.positionKm);
  const flat = transferAt(departureRadiusKm);
  const outward = r2 > departureRadiusKm;
  const alongDeparture = cross(pole, unit(leg.positionKm));
  const flatDepartureKmS = norm(sub(scale(alongDeparture, flat.vDepart), leg.velocityKmS));
  const flatCaptureKmS = Math.abs(captureSignedFor(flat.vInfArrive));
  const departureDvKmS = norm(leg.impulse);
  const thrustDirection = departureDvKmS > 0
    ? scale(leg.impulse, 1 / departureDvKmS)
    : ZERO_VEC;
  const departureBurn: TransferBurn = {
    kind: 'departure',
    label: `Depart ${departure.label}`,
    epochJd: leg.departureEpochJd,
    dvKmS: departureDvKmS,
    impulseDvKmS: departureDvKmS,
    heliocentricDvKmS: flatDepartureKmS,
    frame: 'body-relative',
    referenceId: parent.id,
    sense: senseOf(leg.impulse, leg.velocityKmS),
    direction: thrustDirection,
    localBurn: {
      referenceId: parent.id,
      positionKm: leg.positionKm,
      velocityKmS: leg.velocityKmS,
      thrustDirection,
    },
    vInfKmS: flat.vInfDepart,
    boundSemiMajorKm: leg.semiMajorKm,
  };

  const captureSigned = captureSignedFor(leg.relativeKmS);
  const captureBurn: TransferBurn = {
    kind: 'capture',
    label: holds ? `Capture at ${moon.display_name}` : `Match ${moon.display_name}'s velocity`,
    epochJd: leg.arrivalEpochJd,
    dvKmS: Math.abs(captureSigned),
    impulseDvKmS: Math.abs(captureSigned),
    heliocentricDvKmS: flat.vInfArrive,
    frame: holds ? 'body-relative' : 'heliocentric',
    referenceId: moon.id,
    sense: holds
      ? captureSigned >= 0 ? 'retrograde' : 'prograde'
      : outward ? 'prograde' : 'retrograde',
    direction: leg.captureDirection,
    localBurn: null,
    vInfKmS: leg.relativeKmS,
    boundSemiMajorKm: null,
  };

  // A moon of the body the ship is orbiting is reached on a bound ellipse
  // inside that body's well, not by leaving it, so there is no climb out to
  // price and the departure stays the instant push.
  const burns: readonly TransferBurn[] = [departureBurn, captureBurn];
  const totalDvKmS = burns.reduce((sum, burn) => sum + burn.dvKmS, 0);
  // The flat trip stays the row's second figure - it is what the same two
  // pushes cost with no turn in them - and the difference between the two is
  // what the turn costs.
  const flatTotalKmS = flatDepartureKmS + flatCaptureKmS;
  const timeOfFlightDays = leg.arrivalEpochJd - leg.departureEpochJd;
  return {
    destinationId: moon.id,
    destinationName: moon.display_name,
    burns,
    captureAltitudeKm: captureAltitudeKmFor(moon, ctx),
    totalDvKmS,
    heliocentricDvKmS: flatTotalKmS,
    textbookFreeDvKmS: flatTotalKmS,
    textbookTotalDvKmS: flatTotalKmS,
    textbookTimeOfFlightDays: flat.timeOfFlightDays,
    timeOfFlightDays,
    departureEpochJd: leg.departureEpochJd,
    arrivalEpochJd: leg.arrivalEpochJd,
    windowWaitDays: leg.departureEpochJd - ctx.epochJd,
    synodicPeriodDays: leg.windowPeriodDays,
    departureRadiusKm,
    arrivalRadiusKm: leg.arrivalRadiusKm,
    transferSemiMajorKm: leg.semiMajorKm,
    planeTiltDeg: mismatchDeg,
    planeChangeEnd: leg.turnAtDeparture ? 'departure' : 'arrival',
    planeChangeDvKmS: totalDvKmS - flatTotalKmS,
  };
}

/** The coplanar Hohmann between circular orbits at two heliocentric radii,
 *  and nothing about the wells at either end. */
export interface TextbookTransfer {
  readonly semiMajorKm: number;
  readonly departSpeedKmS: number;
  readonly arriveSpeedKmS: number;
  readonly vInfDepartKmS: number;
  readonly vInfArriveKmS: number;
  /** Excess speed out plus excess speed in: the total with no well at either end. */
  readonly freeDvKmS: number;
  readonly timeOfFlightDays: number;
  readonly timeOfFlightSeconds: number;
  /** Mean motion of a circular orbit at the arrival radius, which is what sets
   *  the phase the window waits for. */
  readonly arrivalMeanMotionRadPerS: number;
  readonly outward: boolean;
}

/** The figure the map has quoted every heliocentric row with, as its own
 *  function, or the reason the pair has no such transfer. */
export function textbookTransfer(r1Km: number, r2Km: number): TextbookTransfer | string {
  if (!(Number.isFinite(r1Km) && r1Km > 0) || !(Number.isFinite(r2Km) && r2Km > 0)) {
    return 'no radius to transfer between';
  }
  if (Math.abs(r2Km - r1Km) / r1Km < 1e-6) {
    return 'the same heliocentric radius, so no transfer and no window';
  }
  const semiMajorKm = (r1Km + r2Km) / 2;
  const vCircle1 = Math.sqrt(GM_SUN_KM3_S2 / r1Km);
  const vCircle2 = Math.sqrt(GM_SUN_KM3_S2 / r2Km);
  const departSpeedKmS = Math.sqrt(GM_SUN_KM3_S2 * (2 / r1Km - 1 / semiMajorKm));
  const arriveSpeedKmS = Math.sqrt(GM_SUN_KM3_S2 * (2 / r2Km - 1 / semiMajorKm));
  const vInfDepartKmS = Math.abs(departSpeedKmS - vCircle1);
  const vInfArriveKmS = Math.abs(vCircle2 - arriveSpeedKmS);
  const timeOfFlightSeconds =
    Math.PI * Math.sqrt((semiMajorKm * semiMajorKm * semiMajorKm) / GM_SUN_KM3_S2);
  return {
    semiMajorKm,
    departSpeedKmS,
    arriveSpeedKmS,
    vInfDepartKmS,
    vInfArriveKmS,
    freeDvKmS: vInfDepartKmS + vInfArriveKmS,
    timeOfFlightDays: timeOfFlightSeconds / SECONDS_PER_DAY,
    timeOfFlightSeconds,
    arrivalMeanMotionRadPerS: Math.sqrt(GM_SUN_KM3_S2 / (r2Km * r2Km * r2Km)),
    outward: r2Km > r1Km,
  };
}

/** Stumpff c2 and c3, in series within 1e-6 of zero where the closed forms
 *  cancel to nothing. */
function stumpff(z: number): { readonly c2: number; readonly c3: number } {
  if (z > 1e-6) {
    const s = Math.sqrt(z);
    return { c2: (1 - Math.cos(s)) / z, c3: (s - Math.sin(s)) / Math.sqrt(z * z * z) };
  }
  if (z < -1e-6) {
    const s = Math.sqrt(-z);
    return { c2: (1 - Math.cosh(s)) / z, c3: (Math.sinh(s) - s) / Math.sqrt(-z * -z * -z) };
  }
  return { c2: 1 / 2 - z / 24 + (z * z) / 720, c3: 1 / 6 - z / 120 + (z * z) / 5040 };
}

/** Degrees either side of half a revolution where every plane through the two
 *  positions solves, so the solver refuses rather than prices. */
export const PLANE_SINGULAR_DEG = 1.0;

const LAMBERT_MAX_ITER = 96;
const LAMBERT_TOLERANCE = 1e-6;
const RAD_TO_DEG = 180 / Math.PI;
const ECLIPTIC_POLE: Vec3 = { x: 0, y: 0, z: 1 };

export type LambertResult =
  | {
      readonly ok: true;
      readonly v1: Vec3;
      readonly v2: Vec3;
      readonly transferAngleDeg: number;
      readonly iterations: number;
      readonly timeOfFlightSeconds: number;
    }
  | { readonly ok: false; readonly why: string; readonly transferAngleDeg: number | null };

/** Single-revolution Lambert by universal variables, bisecting on psi over
 *  [-4pi^2, 4pi^2] with the transfer prograde about `poleHint`. */
export function lambert(
  r1: Vec3,
  r2: Vec3,
  tofSeconds: number,
  gm: number,
  poleHint: Vec3 = ECLIPTIC_POLE,
): LambertResult {
  const r1n = norm(r1);
  const r2n = norm(r2);
  if (!(r1n > 0) || !(r2n > 0) || !(tofSeconds > 0) || !(gm > 0)) {
    return { ok: false, why: 'degenerate input', transferAngleDeg: null };
  }
  const cosSweep = clampUnit(dot(r1, r2) / (r1n * r2n));
  const prograde = dot(cross(r1, r2), poleHint) >= 0;
  const sweepRad = prograde ? Math.acos(cosSweep) : TWO_PI - Math.acos(cosSweep);
  const transferAngleDeg = sweepRad * RAD_TO_DEG;
  if (Math.abs(transferAngleDeg - 180) < PLANE_SINGULAR_DEG) {
    return {
      ok: false,
      why: `transfer angle ${transferAngleDeg.toFixed(3)} deg is within ${PLANE_SINGULAR_DEG} deg of half a revolution`,
      transferAngleDeg,
    };
  }
  if (transferAngleDeg < PLANE_SINGULAR_DEG || transferAngleDeg > 360 - PLANE_SINGULAR_DEG) {
    return {
      ok: false,
      why: `transfer angle ${transferAngleDeg.toFixed(3)} deg is within ${PLANE_SINGULAR_DEG} deg of collinear the other way`,
      transferAngleDeg,
    };
  }
  const chord = (sweepRad < Math.PI ? 1 : -1) * Math.sqrt(r1n * r2n * (1 + cosSweep));
  if (chord === 0) return { ok: false, why: 'no transfer plane', transferAngleDeg };

  let psi = 0;
  let psiLow = -4 * Math.PI * Math.PI;
  let psiHigh = 4 * Math.PI * Math.PI;
  let terms = stumpff(0);
  let y = 0;
  let elapsed = 0;
  let iterations = 0;
  let converged = false;
  for (; iterations < LAMBERT_MAX_ITER; iterations++) {
    terms = stumpff(psi);
    y = r1n + r2n + (chord * (psi * terms.c3 - 1)) / Math.sqrt(terms.c2);
    if (chord > 0 && y < 0) {
      // Raise the floor until the chord is reachable at this psi.
      let guard = 0;
      while (y < 0 && guard++ < 64) {
        psiLow = psi;
        psi = 0.8 * psi + 0.2 * psiHigh;
        terms = stumpff(psi);
        y = r1n + r2n + (chord * (psi * terms.c3 - 1)) / Math.sqrt(terms.c2);
      }
      if (y < 0) return { ok: false, why: 'no y above zero at any psi', transferAngleDeg };
    }
    const chi = Math.sqrt(y / terms.c2);
    elapsed = (chi * chi * chi * terms.c3 + chord * Math.sqrt(y)) / Math.sqrt(gm);
    if (Math.abs(elapsed - tofSeconds) <= LAMBERT_TOLERANCE * Math.max(1, tofSeconds)) {
      converged = true;
      break;
    }
    if (elapsed <= tofSeconds) psiLow = psi;
    else psiHigh = psi;
    psi = (psiHigh + psiLow) / 2;
  }
  if (!converged) {
    return {
      ok: false,
      why: `no convergence in ${iterations} iterations: ${elapsed.toExponential(3)} s against ${tofSeconds.toExponential(3)} s`,
      transferAngleDeg,
    };
  }
  const f = 1 - y / r1n;
  const gDot = 1 - y / r2n;
  const g = chord * Math.sqrt(y / gm);
  if (!(Math.abs(g) > 0)) return { ok: false, why: 'degenerate g', transferAngleDeg };
  return {
    ok: true,
    v1: scale(sub(r2, scale(r1, f)), 1 / g),
    v2: scale(sub(scale(r2, gDot), r1), 1 / g),
    transferAngleDeg,
    iterations,
    timeOfFlightSeconds: elapsed,
  };
}

/** One Lambert leg between two rows' real states, priced from and into the
 *  parking orbits at each end. */
export interface PricedLeg {
  readonly departureEpochJd: number;
  readonly arrivalEpochJd: number;
  readonly timeOfFlightDays: number;
  readonly departureDvKmS: number;
  readonly captureDvKmS: number;
  readonly totalDvKmS: number;
  readonly vInfDepartKmS: number;
  readonly vInfArriveKmS: number;
  readonly departureAsymptote: Vec3;
  readonly arrivalAsymptote: Vec3;
  readonly departurePositionKm: Vec3;
  readonly arrivalPositionKm: Vec3;
  readonly departureVelocityKmS: Vec3;
  readonly arrivalVelocityKmS: Vec3;
  readonly transferAngleDeg: number;
  readonly capturesIntoOrbit: boolean;
}

/** One leg on the real orbits, priced from the parking orbits at each end:
 *  a circular departure and `holdsAnOrbit`'s arrival rule. */
export function priceLeg(
  origin: BodyRow,
  destination: BodyRow,
  departureEpochJd: number,
  timeOfFlightDays: number,
  ctx: TransferContext,
): PricedLeg | string {
  return priceLegAt(
    (epochJd) => heliocentric(origin, ctx.rows, epochJd),
    gmKm3S2(origin),
    origin.radius_m / 1000,
    destination,
    departureEpochJd,
    timeOfFlightDays,
    ctx,
  );
}

/** `priceLeg`'s body, taking the departure side as a position-and-velocity
 *  function rather than a registry row: a row supplies one through its own
 *  ephemeris, a ship free of every sphere through `stateFromWell`. `originGm`
 *  zero collapses the well term below to the plain excess speed, which is what
 *  a departure with no well to climb costs. */
function priceLegAt(
  originStateAt: (epochJd: number) => State,
  originGm: number,
  originSurfaceRadiusKm: number,
  destination: BodyRow,
  departureEpochJd: number,
  timeOfFlightDays: number,
  ctx: TransferContext,
): PricedLeg | string {
  if (!(timeOfFlightDays > 0)) return 'a transfer takes time';
  if (destination.orbit !== null && destination.orbit.parent_relative) {
    return 'a leg is priced between two heliocentric orbits';
  }
  const departureState = originStateAt(departureEpochJd);
  const arrivalEpochJd = departureEpochJd + timeOfFlightDays;
  const arrivalState = heliocentric(destination, ctx.rows, arrivalEpochJd);
  const solution = lambert(
    departureState.position,
    arrivalState.position,
    timeOfFlightDays * SECONDS_PER_DAY,
    GM_SUN_KM3_S2,
  );
  if (!solution.ok) return solution.why;
  const vInfDepart = sub(solution.v1, departureState.velocity);
  const vInfArrive = sub(solution.v2, arrivalState.velocity);
  const vInfDepartKmS = norm(vInfDepart);
  const vInfArriveKmS = norm(vInfArrive);
  const parkingAltitudeKm = ctx.parkingAltitudeKm ?? DEFAULT_PARKING_ALTITUDE_KM;
  const departureRadiusKm = originSurfaceRadiusKm + parkingAltitudeKm;
  const captureRadiusKm = destination.radius_m / 1000 + captureAltitudeKmFor(destination, ctx);
  const departureGm = originGm;
  const captureGm = gmKm3S2(destination);
  const departureCircle = departureRadiusKm > 0 ? Math.sqrt(departureGm / departureRadiusKm) : 0;
  const captureCircle = captureRadiusKm > 0 ? Math.sqrt(captureGm / captureRadiusKm) : 0;
  const capturesIntoOrbit = holdsAnOrbit(destination, captureRadiusKm, ctx.rows);
  const departureDvKmS = wellBurnKmS(
    departureGm,
    departureRadiusKm,
    departureCircle,
    vInfDepartKmS,
  );
  const captureDvKmS = capturesIntoOrbit
    ? wellBurnKmS(captureGm, captureRadiusKm, captureCircle, vInfArriveKmS)
    : vInfArriveKmS;
  return {
    departureEpochJd,
    arrivalEpochJd,
    timeOfFlightDays,
    departureDvKmS,
    captureDvKmS,
    totalDvKmS: departureDvKmS + captureDvKmS,
    vInfDepartKmS,
    vInfArriveKmS,
    departureAsymptote: unit(vInfDepart),
    arrivalAsymptote: unit(vInfArrive),
    departurePositionKm: departureState.position,
    arrivalPositionKm: arrivalState.position,
    departureVelocityKmS: solution.v1,
    arrivalVelocityKmS: solution.v2,
    transferAngleDeg: solution.transferAngleDeg,
    capturesIntoOrbit,
  };
}

/** The coarse grid, the refinement and the time-of-flight range the search
 *  covers, as shares of the synodic period and of the textbook time of flight. */
const WINDOW_DEPARTURE_STEPS = 48;
const WINDOW_TOF_STEPS = 32;
const WINDOW_TOF_LO_FRACTION = 0.5;
const WINDOW_REFINE_ROUNDS = 6;
const WINDOW_REFINE_STEPS = 6;
const WINDOW_REFINE_SHRINK = 3;

/** A ship free of every sphere: the shortest flight its search tries, as a
 *  share of the textbook one from its radius, and what a wait on its coast has
 *  to save over leaving now, as a share of leaving now's cost and at least in
 *  km/s. */
const COAST_TOF_LO_FRACTION = 0.02;
const COAST_WAIT_MUST_SAVE_SHARE = 0.03;
const COAST_WAIT_MUST_SAVE_KMS = 0.01;

/** Solves between two yields of the sliced search. A caller checks its frame
 *  budget between yields, so this is how far past the budget one slice can run:
 *  24 solves is under a millisecond here and a few on a loaded machine. */
const WINDOW_SOLVE_SLICE = 24;

export interface WindowSearchOptions {
  readonly spanDays?: number;
  readonly timeOfFlightLoDays?: number;
  readonly timeOfFlightHiDays?: number;
  /** Multiple of the flat transfer's own time of flight the search stops at,
   *  for a caller that wants the cap somewhere other than where it ships. */
  readonly timeOfFlightCapFraction?: number;
  readonly departureSteps?: number;
  readonly timeOfFlightSteps?: number;
  readonly refineRounds?: number;
  readonly refineSteps?: number;
}

export interface TransferWindow extends PricedLeg {
  readonly waitDays: number;
  readonly synodicPeriodDays: number;
  readonly solves: number;
  readonly refused: number;
}

/** The cheapest departure in one span, driven to the end in one call. */
export function findWindow(
  origin: BodyRow,
  destination: BodyRow,
  fromJd: number,
  ctx: TransferContext,
  options: WindowSearchOptions = {},
): TransferWindow | string {
  const search = findWindowSliced(origin, destination, fromJd, ctx, options);
  let step = search.next();
  while (!step.done) step = search.next();
  return step.value;
}

/** The cheapest departure in one span: a coarse grid over departure day and
 *  time of flight, then a shrinking local search round the best cell. Yields
 *  every `WINDOW_SOLVE_SLICE` solves so a caller on a frame budget can stop. */
export function* findWindowSliced(
  origin: BodyRow,
  destination: BodyRow,
  fromJd: number,
  ctx: TransferContext,
  options: WindowSearchOptions = {},
): Generator<void, TransferWindow | string, void> {
  const originOrbit = origin.orbit;
  if (originOrbit === null) return 'the centre of every orbit';
  if (originOrbit.parent_relative) return 'a window is searched between two heliocentric orbits';
  return yield* searchWindow(
    (epochJd) => heliocentric(origin, ctx.rows, epochJd),
    gmKm3S2(origin),
    origin.radius_m / 1000,
    originOrbit.a_au * AU_KM,
    destination,
    fromJd,
    ctx,
    options,
  );
}

/** The two `lambert` refusal strings that mean the transfer angle landed in
 *  `PLANE_SINGULAR_DEG` of a half revolution, which the singular-band reason
 *  below reads for. */
function isSingularBandRefusal(why: string): boolean {
  return why.includes('deg of half a revolution') || why.includes('deg of collinear the other way');
}

/** `findWindowSliced`'s search, taking the departure side as a state function
 *  rather than a registry row so a ship free of every sphere can carry its
 *  own conic through the same grid (`planRealOrbitsFromState`). `originGm`
 *  zero is passed straight through to `priceLegAt`. */
function* searchWindow(
  originStateAt: (epochJd: number) => State,
  originGm: number,
  originSurfaceRadiusKm: number,
  originRadiusKm: number,
  destination: BodyRow,
  fromJd: number,
  ctx: TransferContext,
  options: WindowSearchOptions,
): Generator<void, TransferWindow | string, void> {
  const destinationOrbit = destination.orbit;
  if (destinationOrbit === null) return 'the centre of every orbit';
  if (destinationOrbit.parent_relative) return 'a window is searched between two heliocentric orbits';
  const r1 = originRadiusKm;
  const r2 = destinationOrbit.a_au * AU_KM;
  const book = textbookTransfer(r1, r2);
  if (typeof book === 'string') return book;
  const n1 = Math.sqrt(GM_SUN_KM3_S2 / (r1 * r1 * r1));
  const rate = book.arrivalMeanMotionRadPerS - n1;
  if (rate === 0) return 'the same mean motion, so no window ever opens';
  const synodicPeriodDays = TWO_PI / Math.abs(rate) / SECONDS_PER_DAY;

  const spanDays = options.spanDays ?? synodicPeriodDays;
  const tofLoDays = options.timeOfFlightLoDays ?? book.timeOfFlightDays * WINDOW_TOF_LO_FRACTION;
  const capFraction = options.timeOfFlightCapFraction ?? WINDOW_TOF_CAP_FRACTION;
  const tofHiDays = options.timeOfFlightHiDays ?? book.timeOfFlightDays * capFraction;
  const departureSteps = options.departureSteps ?? WINDOW_DEPARTURE_STEPS;
  const tofSteps = options.timeOfFlightSteps ?? WINDOW_TOF_STEPS;
  const refineRounds = options.refineRounds ?? WINDOW_REFINE_ROUNDS;
  const refineSteps = options.refineSteps ?? WINDOW_REFINE_STEPS;

  let best: PricedLeg | null = null;
  let solves = 0;
  let refused = 0;
  let refusedNearSingular = 0;
  const priceAt = (departJd: number, tofDays: number): PricedLeg | null => {
    solves++;
    const leg = priceLegAt(
      originStateAt, originGm, originSurfaceRadiusKm, destination, departJd, tofDays, ctx,
    );
    if (typeof leg === 'string') {
      refused++;
      if (isSingularBandRefusal(leg)) refusedNearSingular++;
      return null;
    }
    return leg;
  };

  // No departure steps is a search over the flight time alone, leaving at `fromJd`.
  const departureStepDays = departureSteps > 0 ? spanDays / departureSteps : 0;
  const tofStepDays = (tofHiDays - tofLoDays) / tofSteps;
  let sinceYield = 0;
  for (let i = 0; i <= departureSteps; i++) {
    for (let j = 0; j <= tofSteps; j++) {
      const leg = priceAt(fromJd + i * departureStepDays, tofLoDays + j * tofStepDays);
      if (leg !== null && (best === null || leg.totalDvKmS < best.totalDvKmS)) best = leg;
      if (++sinceYield >= WINDOW_SOLVE_SLICE) {
        sinceYield = 0;
        yield;
      }
    }
  }
  if (best === null) {
    return refused > 0 && refusedNearSingular === refused
      ? `no crossing to it opens: every solve in this window landed within ${PLANE_SINGULAR_DEG} degrees of half a revolution, where the solver refuses`
      : 'no crossing to it opens before the two line up again';
  }

  let departureSpanDays = departureStepDays;
  let tofSpanDays = tofStepDays;
  for (let round = 0; round < refineRounds; round++) {
    const centreDepart = best.departureEpochJd;
    const centreTof = best.timeOfFlightDays;
    for (let i = -refineSteps; i <= refineSteps; i++) {
      if (i !== 0 && departureSpanDays === 0) continue;
      for (let j = -refineSteps; j <= refineSteps; j++) {
        if (i === 0 && j === 0) continue;
        const departJd = centreDepart + (i * departureSpanDays) / refineSteps;
        const tofDays = centreTof + (j * tofSpanDays) / refineSteps;
        if (departJd < fromJd || tofDays <= 0) continue;
        const leg = priceAt(departJd, tofDays);
        if (leg !== null && leg.totalDvKmS < best.totalDvKmS) best = leg;
        if (++sinceYield >= WINDOW_SOLVE_SLICE) {
          sinceYield = 0;
          yield;
        }
      }
    }
    departureSpanDays /= WINDOW_REFINE_SHRINK;
    tofSpanDays /= WINDOW_REFINE_SHRINK;
  }
  return {
    ...best,
    waitDays: best.departureEpochJd - fromJd,
    synodicPeriodDays,
    solves,
    refused,
  };
}

/** The parking orbit the plan places to leave along `asymptote`: the plane
 *  holding the asymptote whose pole sits closest to the ecliptic pole. Only a
 *  circular orbit the plan is placing has this freedom; an orbit the ship is
 *  already on has to buy the turn out of the orbit it is on. */
function parkingPlaneFor(well: DepartureWell, asymptote: Vec3): DepartureWell {
  const aim = unit(asymptote);
  const raw = sub(ECLIPTIC_POLE, scale(aim, dot(ECLIPTIC_POLE, aim)));
  const pole = norm(raw) > 1e-9 ? unit(raw) : unit(cross(aim, { x: 1, y: 0, z: 0 }));
  const radial = unit(cross(pole, aim));
  const positionKm = scale(radial, well.radiusKm);
  const velocityKmS = scale(unit(cross(pole, radial)), well.speedKmS);
  return {
    ...well,
    positionKm,
    velocityKmS,
    orbit: closedOrbitOf(well.gm, positionKm, velocityKmS, well.surfaceRadiusKm),
  };
}

function planOne(
  departure: Departure,
  destination: BodyRow,
  ctx: TransferContext,
): TransferPlan | string {
  const run = planOneSliced(departure, destination, ctx);
  let step = run.next();
  while (!step.done) step = run.next();
  return step.value;
}

/** One destination, yielding inside its window search so a caller on a frame
 *  budget can stop between solves. */
function* planOneSliced(
  departure: Departure,
  destination: BodyRow,
  ctx: TransferContext,
): Generator<void, TransferPlan | string, void> {
  const orbit = destination.orbit;
  if (orbit === null) return 'the centre of every orbit';
  if (orbit.parent_relative) {
    const parent = destination.parent === null
      ? undefined
      : bodyById(ctx.rows, destination.parent);
    const well = departure.well;
    if (destination.id === departure.hostId) return 'the ship is already there';
    if (parent !== undefined && well !== null && parent.id === departure.bodyId) {
      return yield* planMoonTransfer(departure, well, parent, destination, ctx);
    }
    const parentName = parent?.display_name ?? destination.parent ?? 'another body';
    return `orbits ${parentName}: plan it from ${parentName}, not from ${departure.label}`;
  }
  if (destination.id === departure.hostId) return 'the ship is already there';
  if (destination.id === departure.bodyId) {
    return `orbiting ${departure.hostLabel}, not parked at ${destination.display_name}: burn down to a ${destination.display_name} parking orbit first`;
  }

  const r1 = departure.radiusKm;
  const r2 = orbit.a_au * AU_KM;
  const book = textbookTransfer(r1, r2);
  if (typeof book === 'string') return book;
  if (departure.bodyId === null) {
    if (ctx.forceFlatDeepSpace === true) return planFlat(departure, destination, ctx, book);
    return yield* planRealOrbitsFromState(departure, destination, ctx, book);
  }
  const originRow = bodyById(ctx.rows, departure.bodyId);
  const departureWell = departure.well;
  if (originRow !== undefined && departureWell !== null) {
    return yield* planRealOrbits(departure, originRow, departureWell, destination, ctx, book);
  }
  return planFlat(departure, destination, ctx, book);
}

/** The flat two-burn plan: the fallback for a departure whose own host body
 *  cannot be resolved in the registry, since there is then no row to carry a
 *  real-orbit window search from. */
function planFlat(
  departure: Departure,
  destination: BodyRow,
  ctx: TransferContext,
  book: TextbookTransfer,
): TransferPlan | string {
  const r1 = departure.radiusKm;
  const r2 = destination.orbit === null ? 0 : destination.orbit.a_au * AU_KM;
  const aTransferKm = book.semiMajorKm;
  const vDepart = book.departSpeedKmS;
  const vInfDepart = book.vInfDepartKmS;
  const vInfArrive = book.vInfArriveKmS;
  const outward = book.outward;
  const tofSeconds = book.timeOfFlightSeconds;
  const timeOfFlightDays = book.timeOfFlightDays;

  // The window: the destination has to lead the ship by exactly the angle it
  // sweeps while the ship is on the ellipse, less the half turn the ship takes.
  const n1 = departure.meanMotionRadPerS;
  const n2 = book.arrivalMeanMotionRadPerS;
  const rate = n2 - n1;
  if (rate === 0) return 'the same mean motion, so no window ever opens';
  const phaseWanted = wrapTwoPi(Math.PI - n2 * tofSeconds);
  const heliocentricPhaseAt = (epochJd: number): number => {
    const origin = departure.positionAt(epochJd);
    const target = heliocentric(destination, ctx.rows, epochJd).position;
    return wrapTwoPi(Math.atan2(target.y, target.x) - Math.atan2(origin.y, origin.x));
  };
  const waitDays = solveWindowDays(heliocentricPhaseAt, ctx, phaseWanted, rate);
  const synodicPeriodDays = TWO_PI / Math.abs(rate) / SECONDS_PER_DAY;
  const departureEpochJd = ctx.epochJd + waitDays;
  const arrivalEpochJd = departureEpochJd + timeOfFlightDays;

  const positionAtDeparture = departure.positionAt(departureEpochJd);
  const velocityAtDeparture = departure.velocityAt(departureEpochJd);
  const alongDeparture = progradeAt(positionAtDeparture);

  let departureBurn: TransferBurn;
  const well = departure.well;
  const departureAsymptote = outward ? alongDeparture : scale(alongDeparture, -1);
  if (well !== null) {
    const referenceId = departure.bodyId ?? 'sol';
    const sweep = sweptOrbit(well, ctx);
    const impulse = sweep === null
      ? departureImpulse(well, referenceId, vInfDepart, departureAsymptote)
      : cheapestOnOrbit(well, sweep, referenceId, vInfDepart, departureAsymptote);
    if (typeof impulse === 'string') return impulse;
    departureBurn = {
      kind: 'departure',
      label: `Depart ${departure.label}`,
      epochJd: departureEpochJd,
      dvKmS: impulse.dvKmS,
      impulseDvKmS: impulse.dvKmS,
      heliocentricDvKmS: vInfDepart,
      frame: 'body-relative',
      referenceId,
      sense: impulse.sense,
      direction: departureAsymptote,
      localBurn: impulse.point,
      vInfKmS: vInfDepart,
      boundSemiMajorKm: null,
    };
  } else {
    const required = scale(alongDeparture, vDepart);
    const impulse = sub(required, velocityAtDeparture);
    const dv = norm(impulse);
    departureBurn = {
      kind: 'departure',
      label: 'Depart',
      epochJd: departureEpochJd,
      dvKmS: dv,
      impulseDvKmS: dv,
      heliocentricDvKmS: dv,
      frame: 'heliocentric',
      referenceId: 'sol',
      sense: senseOf(impulse, velocityAtDeparture),
      direction: unit(impulse),
      localBurn: null,
      vInfKmS: vInfDepart,
      boundSemiMajorKm: null,
    };
  }

  const arrivalCentre = heliocentric(destination, ctx.rows, arrivalEpochJd).position;
  const alongArrival = progradeAt(arrivalCentre);
  const captureRadiusKm = destination.radius_m / 1000 + captureAltitudeKmFor(destination, ctx);
  const captureGm = gmKm3S2(destination);
  const captureCircle = captureRadiusKm > 0 ? Math.sqrt(captureGm / captureRadiusKm) : 0;
  const captureBodyRelative = captureGm > 0 && captureRadiusKm > 0;
  // Signed the same way a departure is: positive is speed the ship has to shed
  // at periapsis, which is what dropping into orbit always is.
  const captureSigned = captureBodyRelative
    ? wellBurnKmS(captureGm, captureRadiusKm, captureCircle, vInfArrive)
    : vInfArrive;
  const captureBurn: TransferBurn = {
    kind: 'capture',
    label: `Capture at ${destination.display_name}`,
    epochJd: arrivalEpochJd,
    dvKmS: Math.abs(captureSigned),
    impulseDvKmS: Math.abs(captureSigned),
    heliocentricDvKmS: vInfArrive,
    frame: captureBodyRelative ? 'body-relative' : 'heliocentric',
    referenceId: destination.id,
    sense: captureBodyRelative
      ? captureSigned >= 0
        ? 'retrograde'
        : 'prograde'
      : outward
        ? 'prograde'
        : 'retrograde',
    direction: outward ? alongArrival : scale(alongArrival, -1),
    localBurn: null,
    vInfKmS: vInfArrive,
    boundSemiMajorKm: null,
  };

  const burns: readonly TransferBurn[] = [
    pricedDeparture(departureBurn, ctx),
    pricedCapture(captureBurn, ctx),
  ];
  const totalDvKmS = burns.reduce((sum, burn) => sum + burn.dvKmS, 0);
  return {
    destinationId: destination.id,
    destinationName: destination.display_name,
    burns,
    captureAltitudeKm: captureAltitudeKmFor(destination, ctx),
    totalDvKmS,
    heliocentricDvKmS: burns.reduce((sum, burn) => sum + burn.heliocentricDvKmS, 0),
    textbookFreeDvKmS: book.freeDvKmS,
    textbookTotalDvKmS: burns.reduce((sum, burn) => sum + burn.impulseDvKmS, 0),
    textbookTimeOfFlightDays: book.timeOfFlightDays,
    timeOfFlightDays,
    departureEpochJd,
    arrivalEpochJd,
    windowWaitDays: waitDays,
    synodicPeriodDays,
    departureRadiusKm: r1,
    arrivalRadiusKm: r2,
    transferSemiMajorKm: aTransferKm,
    // A heliocentric leg is flown in the ecliptic from a parking orbit the
    // model builds in the ecliptic, so there is no second plane here the way
    // there is at a moon; the assumptions line carries the tilt the model
    // leaves out for these.
    planeTiltDeg: null,
    planeChangeEnd: null,
    planeChangeDvKmS: 0,
  };
}

/** The plan on the real orbits: the window search names the departure date and
 *  the flight, the Lambert conic through the two real positions names both
 *  excess speeds, and the ship's own well prices the departure burn. The flat
 *  transfer rides along as the second figure the panel shows. */
function* planRealOrbits(
  departure: Departure,
  originRow: BodyRow,
  well: DepartureWell,
  destination: BodyRow,
  ctx: TransferContext,
  book: TextbookTransfer,
): Generator<void, TransferPlan | string, void> {
  const window = yield* findWindowSliced(
    originRow, destination, ctx.epochJd, ctx, ctx.windowSearch ?? {},
  );
  if (typeof window === 'string') return window;

  const referenceId = departure.bodyId ?? 'sol';
  const asymptote = window.departureAsymptote;
  const sweep = sweptOrbit(well, ctx);
  const departureWell = sweep === null && well.circular ? parkingPlaneFor(well, asymptote) : well;
  const impulse = sweep === null
    ? departureImpulse(departureWell, referenceId, window.vInfDepartKmS, asymptote)
    : cheapestOnOrbit(well, sweep, referenceId, window.vInfDepartKmS, asymptote);
  if (typeof impulse === 'string') return impulse;
  const departureBurn: TransferBurn = {
    kind: 'departure',
    label: `Depart ${departure.label}`,
    epochJd: window.departureEpochJd,
    dvKmS: impulse.dvKmS,
    impulseDvKmS: impulse.dvKmS,
    heliocentricDvKmS: window.vInfDepartKmS,
    frame: 'body-relative',
    referenceId,
    sense: impulse.sense,
    direction: asymptote,
    localBurn: impulse.point,
    vInfKmS: window.vInfDepartKmS,
    boundSemiMajorKm: null,
  };

  // The capture kills the excess the ship arrives with, so the velocity change
  // points back along the incoming asymptote.
  const captureDirection = scale(window.arrivalAsymptote, -1);
  const captureBurn: TransferBurn = {
    kind: 'capture',
    label: window.capturesIntoOrbit
      ? `Capture at ${destination.display_name}`
      : `Match ${destination.display_name}'s velocity`,
    epochJd: window.arrivalEpochJd,
    dvKmS: window.captureDvKmS,
    impulseDvKmS: window.captureDvKmS,
    heliocentricDvKmS: window.vInfArriveKmS,
    frame: window.capturesIntoOrbit ? 'body-relative' : 'heliocentric',
    referenceId: destination.id,
    sense: window.capturesIntoOrbit
      ? 'retrograde'
      : senseOf(
          scale(captureDirection, window.vInfArriveKmS),
          heliocentric(destination, ctx.rows, window.arrivalEpochJd).velocity,
        ),
    direction: captureDirection,
    localBurn: null,
    vInfKmS: window.vInfArriveKmS,
    boundSemiMajorKm: null,
  };

  const textbookDepartureKmS = Math.abs(
    wellBurnKmS(well.gm, well.radiusKm, well.speedKmS, book.vInfDepartKmS),
  );
  return finishRealOrbitsPlan(destination, ctx, book, window, departureBurn, textbookDepartureKmS);
}

/**
 * The real-orbits plan for a ship free of every sphere: the same window
 * search and capture rule as a body-based departure, but with no well to
 * climb, so the departure burn is the plain vector difference between the
 * transfer's own required velocity and the ship's actual one, the way
 * `planFlat`'s well-less branch already prices a departure.
 */
function* planRealOrbitsFromState(
  departure: Departure,
  destination: BodyRow,
  ctx: TransferContext,
  book: TextbookTransfer,
): Generator<void, TransferPlan | string, void> {
  const options = ctx.windowSearch ?? {};
  const search = (extra: WindowSearchOptions) => searchWindow(
    (epochJd) => ({ position: departure.positionAt(epochJd), velocity: departure.velocityAt(epochJd) }),
    0,
    0,
    departure.radiusKm,
    destination,
    ctx.epochJd,
    ctx,
    { ...options, ...extra },
  );
  // A ship out here is usually already on its way, so the course from where it
  // is comes first: a textbook transfer from this radius assumes a circular
  // start and puts the ship's own arrival below the default flight-time range.
  const timeOfFlightLoDays = options.timeOfFlightLoDays
    ?? book.timeOfFlightDays * COAST_TOF_LO_FRACTION;
  const leaveNow = yield* search({ spanDays: 0, departureSteps: 0, timeOfFlightLoDays });
  let window: TransferWindow | string;
  if (typeof leaveNow === 'string') {
    // Nothing leaves from here now, so wait for the pair to line up.
    window = yield* search({});
  } else {
    // A wait is offered over the whole span, since a ship not on its way can
    // save most of the trip by waiting, but only when it saves a real share.
    const waited = yield* search({ timeOfFlightLoDays });
    const margin = Math.max(COAST_WAIT_MUST_SAVE_KMS, COAST_WAIT_MUST_SAVE_SHARE * leaveNow.totalDvKmS);
    window = typeof waited !== 'string' && waited.totalDvKmS < leaveNow.totalDvKmS - margin
      ? waited
      : leaveNow;
  }
  if (typeof window === 'string') return window;

  const actualVelocityKmS = departure.velocityAt(window.departureEpochJd);
  const impulse = sub(window.departureVelocityKmS, actualVelocityKmS);
  const departureBurn: TransferBurn = {
    kind: 'departure',
    label: `Depart ${departure.label}`,
    epochJd: window.departureEpochJd,
    dvKmS: norm(impulse),
    impulseDvKmS: norm(impulse),
    heliocentricDvKmS: window.vInfDepartKmS,
    frame: 'heliocentric',
    referenceId: 'sol',
    sense: senseOf(impulse, actualVelocityKmS),
    direction: unit(impulse),
    localBurn: null,
    vInfKmS: window.vInfDepartKmS,
    boundSemiMajorKm: null,
  };
  return finishRealOrbitsPlan(destination, ctx, book, window, departureBurn, book.vInfDepartKmS);
}

/** The tail `planRealOrbits` and `planRealOrbitsFromState` share once each
 *  has its own departure burn: the capture burn, which depends only on the
 *  destination, and the plan's own final figures. */
function finishRealOrbitsPlan(
  destination: BodyRow,
  ctx: TransferContext,
  book: TextbookTransfer,
  window: TransferWindow,
  departureBurn: TransferBurn,
  textbookDepartureKmS: number,
): TransferPlan {
  // The capture kills the excess the ship arrives with, so the velocity change
  // points back along the incoming asymptote.
  const captureDirection = scale(window.arrivalAsymptote, -1);
  const captureBurn: TransferBurn = {
    kind: 'capture',
    label: window.capturesIntoOrbit
      ? `Capture at ${destination.display_name}`
      : `Match ${destination.display_name}'s velocity`,
    epochJd: window.arrivalEpochJd,
    dvKmS: window.captureDvKmS,
    impulseDvKmS: window.captureDvKmS,
    heliocentricDvKmS: window.vInfArriveKmS,
    frame: window.capturesIntoOrbit ? 'body-relative' : 'heliocentric',
    referenceId: destination.id,
    sense: window.capturesIntoOrbit
      ? 'retrograde'
      : senseOf(
          scale(captureDirection, window.vInfArriveKmS),
          heliocentric(destination, ctx.rows, window.arrivalEpochJd).velocity,
        ),
    direction: captureDirection,
    localBurn: null,
    vInfKmS: window.vInfArriveKmS,
    boundSemiMajorKm: null,
  };

  const captureRadiusKm = destination.radius_m / 1000 + captureAltitudeKmFor(destination, ctx);
  const captureGm = gmKm3S2(destination);
  const captureCircleKmS = captureRadiusKm > 0 ? Math.sqrt(captureGm / captureRadiusKm) : 0;
  const textbookCaptureKmS = window.capturesIntoOrbit
    ? Math.abs(wellBurnKmS(captureGm, captureRadiusKm, captureCircleKmS, book.vInfArriveKmS))
    : book.vInfArriveKmS;

  const departureRadiusKm = norm(window.departurePositionKm);
  const departureSpeedKmS = norm(window.departureVelocityKmS);
  const inverseSemiMajor =
    2 / departureRadiusKm - (departureSpeedKmS * departureSpeedKmS) / GM_SUN_KM3_S2;
  const burns: readonly TransferBurn[] = [
    pricedDeparture(departureBurn, ctx),
    pricedCapture(captureBurn, ctx),
  ];
  return {
    destinationId: destination.id,
    destinationName: destination.display_name,
    burns,
    captureAltitudeKm: captureAltitudeKmFor(destination, ctx),
    totalDvKmS: burns.reduce((sum, burn) => sum + burn.dvKmS, 0),
    heliocentricDvKmS: burns.reduce((sum, burn) => sum + burn.heliocentricDvKmS, 0),
    textbookFreeDvKmS: book.freeDvKmS,
    textbookTotalDvKmS: textbookDepartureKmS + textbookCaptureKmS,
    textbookTimeOfFlightDays: book.timeOfFlightDays,
    timeOfFlightDays: window.timeOfFlightDays,
    departureEpochJd: window.departureEpochJd,
    arrivalEpochJd: window.arrivalEpochJd,
    windowWaitDays: window.waitDays,
    synodicPeriodDays: window.synodicPeriodDays,
    departureRadiusKm,
    arrivalRadiusKm: norm(window.arrivalPositionKm),
    transferSemiMajorKm: inverseSemiMajor === 0 ? book.semiMajorKm : 1 / inverseSemiMajor,
    // The turn into the destination's own plane is bought by this transfer, at
    // the departure, so there is no unpriced tilt left to name.
    planeTiltDeg: null,
    planeChangeEnd: null,
    planeChangeDvKmS: 0,
  };
}

/** One destination, or null where the pair has no transfer in this model.
 *  `planTransfers` gives the reason instead of dropping it silently, and
 *  `TRANSFER_ASSUMPTIONS` is the same list the survey hands the panel: a caller
 *  planning a row at a time still has to show what the model leaves out. */
export function planTransfer(
  origin: TransferOrigin,
  destinationId: string,
  ctx: TransferContext,
): TransferPlan | null {
  const departure = resolveDeparture(origin, ctx);
  if (typeof departure === 'string') return null;
  const destination = bodyById(ctx.rows, destinationId);
  if (destination === undefined) return null;
  const plan = planOne(departure, destination, ctx);
  return typeof plan === 'string' ? null : plan;
}

/** What a survey has so far while it is still being priced: the plans that
 *  have landed, the rows that were refused, and the destinations still out. */
export interface SurveyProgress {
  readonly plans: readonly TransferPlan[];
  readonly skipped: readonly TransferSkip[];
  readonly pendingIds: readonly string[];
}

/** Every reachable destination from one origin, cheapest total first, with the
 *  unreachable rows and their reasons beside them. Driven to the end in one
 *  call; `planTransfersSliced` is the same survey a frame at a time. */
export function planTransfers(origin: TransferOrigin, ctx: TransferContext): TransferSurvey {
  const run = planTransfersSliced(origin, ctx);
  let step = run.next();
  while (!step.done) step = run.next();
  return step.value;
}

/** The survey as a generator, yielding what has landed so far every few dozen
 *  solves. The whole survey is a second of solving on the outer system, which
 *  is four replan floors of stall if it is spent in one call. */
export function* planTransfersSliced(
  origin: TransferOrigin,
  ctx: TransferContext,
): Generator<SurveyProgress, TransferSurvey, void> {
  const departure = resolveDeparture(origin, ctx);
  if (typeof departure === 'string') {
    return {
      epochJd: ctx.epochJd,
      originBodyId: null,
      originLabel: 'nowhere',
      hostLabel: 'nowhere',
      originRadiusKm: 0,
      departurePricing: 'free-space',
      plans: [],
      skipped: ctx.rows.map((row) => ({ id: row.id, name: row.display_name, reason: departure })),
      assumptions: TRANSFER_ASSUMPTIONS,
    };
  }
  const plans: TransferPlan[] = [];
  const skipped: TransferSkip[] = [];
  const pendingIds: string[] = ctx.rows.map((row) => row.id);
  const progress: SurveyProgress = { plans, skipped, pendingIds };
  for (const row of ctx.rows) {
    const run = planOneSliced(departure, row, ctx);
    let step = run.next();
    while (!step.done) {
      yield progress;
      step = run.next();
    }
    const plan = step.value;
    if (typeof plan === 'string') skipped.push({ id: row.id, name: row.display_name, reason: plan });
    else plans.push(plan);
    const at = pendingIds.indexOf(row.id);
    if (at >= 0) pendingIds.splice(at, 1);
  }
  plans.sort((a, b) => a.totalDvKmS - b.totalDvKmS);
  const escapeKmS = departure.hostEscapeKmS;
  const well = departure.well;
  const pricing: DeparturePricing = well === null
    ? 'free-space'
    : well.circular
      ? 'circular-point'
      : sweptOrbit(well, ctx) === null ? 'ship-point' : 'cheapest-on-orbit';
  return {
    epochJd: ctx.epochJd,
    originBodyId: departure.bodyId,
    originLabel: departure.label,
    hostLabel: departure.hostLabel,
    originRadiusKm: departure.radiusKm,
    departurePricing: pricing,
    plans,
    skipped,
    assumptions: TRANSFER_ASSUMPTIONS.map((line) => {
      if (line === MOON_ESCAPE_ASSUMPTION && escapeKmS !== null) {
        return `${line} From where the ship is now that is ${escapeKmS.toFixed(4)} km/s.`;
      }
      if (line === DEPARTURE_ASSUMPTION) return line + DEPARTURE_PRICING_NOTES[pricing];
      if (line === IMPULSE_ASSUMPTION && (ctx.shipAccelMps2 ?? 0) > 0) {
        return FINITE_DEPARTURE_ASSUMPTION;
      }
      return line;
    }),
  };
}
